// File: src/pages/CreatePurchaseOrder.jsx
import React, { useEffect, useMemo, useRef, useState, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  Col,
  Form,
  Row,
  Input,
  Label,
  Button,
  Table,
  Spinner,
} from "reactstrap";
import Multiselect from "multiselect-react-dropdown";
import swal from "sweetalert";

import UserContext from "../import/context/Context";
import {
  _Get,
  CreateCustomerList,
  PurchaseOrderList,
  createOrderhistoryviewDATABASE,
  SavePurchaseOrder,
} from "../import/ApiEndPoint/ApiCalling";

import {
  PurchaseProductList_Product,
  ViewOther_Charges,
  WareHouse_Closing_Stock,
  Create_Warehouse_List,
} from "../import/ApiEndPoint/Api";

import "./InvoiceUI.css";

/* ---------------- helpers ---------------- */

const round2 = (n) => Number((Number(n) || 0).toFixed(2));
const ceil2 = (n) => Math.ceil((Number(n) || 0) * 100) / 100;
const toNum = (v, d = 0) => (Number.isFinite(+v) ? +v : d);

const todayStr = () => new Date().toISOString().slice(0, 10);

const parseGstPct = (g) => {
  if (g === null || g === undefined) return 0;
  if (typeof g === "number") return g || 0;

  const s = String(g).trim();
  if (!s) return 0;

  return s
    .split("+")
    .map((x) => Number(x) || 0)
    .reduce((a, b) => a + b, 0);
};

const roundHalfUp = (n) => {
  const value = Number(n) || 0;
  const frac = value - Math.floor(value);
  return frac > 0.49 ? Math.ceil(value) : Math.floor(value);
};

const getSelectedDatabase = (userData = {}) => {
  const financialYearSelected = localStorage.getItem("financialYearSelected");

  return financialYearSelected
    ? `${userData?.database}-${financialYearSelected}`
    : userData?.database || "";
};

const getFinancialYearLabel = (dateValue = new Date()) => {
  let year;
  let month;

  if (typeof dateValue === "string" && dateValue.includes("-")) {
    const [yyyy, mm] = dateValue.split("-");
    year = Number(yyyy);
    month = Number(mm);
  } else {
    const d = new Date(dateValue);
    year = d.getFullYear();
    month = d.getMonth() + 1;
  }

  if (!year || !month) {
    const d = new Date();
    year = d.getFullYear();
    month = d.getMonth() + 1;
  }

  const startYear = month >= 4 ? year : year - 1;
  const endYearShort = String(startYear + 1).slice(-2);

  return `${startYear}-${endYearShort}`;
};

const num = (v) => (Number.isFinite(+v) ? +v : 0);

const normId = (val) => {
  if (!val) return "";
  if (typeof val === "string") return val;
  if (typeof val === "object") {
    if (val._id) return String(val._id);
    if (val.$oid) return String(val.$oid);
  }
  return String(val);
};

// Keep expected 404 responses silent.
// Your backend uses 404 for normal "no data found" cases, so these should not show
// the React red overlay or SweetAlert popup during page load/background checks.
const isAxios404 = (err) =>
  err?.response?.status === 404 || err?.status === 404;

const silentApiError = (err) => {
  if (!isAxios404(err)) {
    console.error("API Error:", err);
  }
};

const firstDefined = (...vals) => {
  for (const value of vals) {
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return undefined;
};

const parseFYStart = (fy) => {
  const startYear = Number(String(fy || "").split("-")[0]);
  if (Number.isFinite(startYear)) return startYear;

  const today = new Date();
  return today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
};

const getPreviousFY = (fy) => {
  const start = parseFYStart(fy) - 1;
  return `${start}-${String(start + 1).slice(-2)}`;
};

const buildSelectedDatabase = (database, financialYear) =>
  `${database}-${financialYear}`;

const safeJson = (value) => {
  if (!value) return null;
  if (typeof value === "object") return value;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const findFYEntry = (container, financialYear) => {
  const data = safeJson(container);
  if (!data) return null;

  if (Array.isArray(data)) {
    return (
      data.find(
        (item) =>
          String(
            item?.financialYear ||
              item?.FinancialYear ||
              item?.fy ||
              item?.year ||
              "",
          ) === String(financialYear),
      ) || null
    );
  }

  if (typeof data === "object") {
    return (
      data[financialYear] ||
      data[String(financialYear).replace("-", "_")] ||
      data[String(financialYear).replace("-", "/")] ||
      null
    );
  }

  return null;
};

const getOpeningFinancialYear = (row = {}) =>
  firstDefined(
    row?._productDetailsFromProductList?.openingFinancialYear,
    row?._productDetailsFromProductList?.OpeningFinancialYear,
    row?._productDetailsFromProductList?.financialYearOpeningYear,
    row?._productDetailsFromProductList?.openingFY,
    row?._productDetailsFromProductList?.OpeningFY,
    row?._productDetailsFromProductList?.openingYear,
    row?.openingFinancialYear,
    row?.OpeningFinancialYear,
    row?.financialYearOpeningYear,
    row?.productId?.openingFinancialYear,
    row?.productId?.OpeningFinancialYear,
    row?.product?.openingFinancialYear,
    row?.product?.OpeningFinancialYear,
  );

const getProductSavedOpening = (row = {}) => {
  const productDetails =
    row?._productDetailsFromProductList ||
    (row?.productId && typeof row.productId === "object"
      ? row.productId
      : {}) ||
    row?.product ||
    {};

  const qty = firstDefined(
    productDetails?.Opening_Stock,
    productDetails?.openingStock,
    productDetails?.openingQty,
    productDetails?.oQty,
    productDetails?.financialYearOpeningStock,
    productDetails?.OpeningStock,
    row?.Opening_Stock,
    row?.openingStock,
    row?.openingQty,
    row?.oQty,
  );

  const rate = firstDefined(
    productDetails?.openingRate,
    productDetails?.Opening_Rate,
    productDetails?.OpeningRate,
    productDetails?.oRate,
    productDetails?.purchaseRate,
    productDetails?.PurchaseRate,
    productDetails?.Product_Purchase_Rate,
    productDetails?.Purchase_Price,
    productDetails?.purchasePrice,
    productDetails?.PurchasePrice,
    productDetails?.costPrice,
    productDetails?.Product_Rate,
    productDetails?.price,
    row?.openingRate,
    row?.oRate,
    row?.price,
  );

  return {
    qty: num(qty),
    rate: num(rate),
  };
};

const extractBackendFYOpening = (row, financialYear) => {
  const directFY = String(
    row?.financialYear ||
      row?.openingFinancialYear ||
      row?.FinancialYear ||
      row?.fy ||
      "",
  );

  if (directFY && directFY === String(financialYear)) {
    const qty = firstDefined(
      row?.financialYearOpeningStock,
      row?.openingStock,
      row?.openingQty,
      row?.Opening_Stock,
      row?.oQty,
    );
    const rate = firstDefined(
      row?.financialYearOpeningRate,
      row?.openingRate,
      row?.oRate,
    );

    if (qty !== undefined || rate !== undefined) {
      return { qty: num(qty), rate: num(rate) };
    }
  }

  const possibleFYContainers = [
    row?.openingStockByFY,
    row?.openingStocks,
    row?.financialYearOpenings,
    row?.openingByFinancialYear,
    row?.fyOpeningStock,
    row?.yearWiseOpeningStock,
    row?.productId?.openingStockByFY,
    row?.productId?.openingStocks,
    row?.productId?.financialYearOpenings,
    row?.productId?.openingByFinancialYear,
  ];

  for (const container of possibleFYContainers) {
    const entry = findFYEntry(container, financialYear);
    if (!entry) continue;

    return {
      qty: num(
        firstDefined(
          entry?.openingStock,
          entry?.openingQty,
          entry?.Opening_Stock,
          entry?.oQty,
          entry?.qty,
          entry?.closingQty,
        ),
      ),
      rate: num(
        firstDefined(
          entry?.openingRate,
          entry?.rate,
          entry?.closingAveRate,
          entry?.closingRate,
        ),
      ),
    };
  }

  return null;
};

const stockSnapshotKey = (database, financialYear) =>
  `FY_CLOSING_STOCK_SNAPSHOT:${database}:${financialYear}`;

const getProductIdForStockKey = (row = {}) =>
  normId(
    row?._productIdResolved ||
      row?._productDetailsFromProductList?._id ||
      row?._productDetailsFromProductList?.id ||
      row?.productId?._id ||
      row?.productId ||
      row?.pId ||
      row?.ProductId ||
      row?.product?._id ||
      "",
  );

const getWarehouseIdForStockKey = (row = {}) =>
  normId(
    row?._warehouseIdResolved ||
      row?.warehouseId?._id ||
      row?.warehouseId ||
      row?.warehouse?._id ||
      row?.warehouse ||
      row?.warehouseId1 ||
      "",
  );

const getProductTitleForStockKey = (row = {}) =>
  String(
    row?.Product_Title ||
      row?._productDetailsFromProductList?.Product_Title ||
      row?._productDetailsFromProductList?.productName ||
      row?.productId?.Product_Title ||
      row?.productName ||
      row?.ProductName ||
      "",
  )
    .trim()
    .toLowerCase();

const getStockLookupKeys = (row = {}) => {
  const warehouseId = getWarehouseIdForStockKey(row);
  const productId = getProductIdForStockKey(row);
  const productTitle = getProductTitleForStockKey(row);
  const keys = [];

  if (warehouseId && productId) keys.push(`${warehouseId}::${productId}`);
  if (warehouseId && productTitle)
    keys.push(`${warehouseId}::title:${productTitle}`);
  if (productId) keys.push(`id:${productId}`);
  if (productTitle) keys.push(`title:${productTitle}`);

  return [...new Set(keys.filter(Boolean))];
};

const findPreviousClosingForRow = (row, previousClosingMap = {}) => {
  for (const key of getStockLookupKeys(row)) {
    if (previousClosingMap?.[key]) return previousClosingMap[key];
  }
  return null;
};

const addPreviousClosingToMap = (map, row, closing) => {
  const keys = getStockLookupKeys(row);
  if (!keys.length) return;

  const payload = {
    closingQty: num(closing?.closingQty),
    closingAveRate: num(closing?.closingAveRate),
    closingTotal: num(closing?.closingTotal),
  };

  keys.forEach((key) => {
    if (!map[key]) {
      map[key] = { ...payload };
      return;
    }

    const oldQty = num(map[key]?.closingQty);
    const oldTotal = num(map[key]?.closingTotal);
    const totalQty = oldQty + payload.closingQty;
    const totalValue = oldTotal + payload.closingTotal;

    map[key] = {
      closingQty: round2(totalQty),
      closingTotal: round2(totalValue),
      closingAveRate: totalQty > 0 ? round2(totalValue / totalQty) : 0,
    };
  });
};

const buildProductDetailsMap = (products = []) => {
  const byId = new Map();
  const byTitle = new Map();
  const byWarehouseProductId = new Map();
  const byWarehouseTitle = new Map();

  (Array.isArray(products) ? products : []).forEach((item) => {
    const productId = normId(item?._id || item?.id || item?.productId || "");
    const title = getProductTitleForStockKey(item);
    const warehouseId = getWarehouseIdForStockKey(item);

    if (productId) byId.set(productId, item);
    if (title && !byTitle.has(title)) byTitle.set(title, item);
    if (warehouseId && productId)
      byWarehouseProductId.set(`${warehouseId}::${productId}`, item);
    if (warehouseId && title)
      byWarehouseTitle.set(`${warehouseId}::${title}`, item);
  });

  return {
    byId,
    byTitle,
    byWarehouseProductId,
    byWarehouseTitle,
    list: Array.isArray(products) ? products : [],
  };
};

const findProductDetailsForStockRow = (row, productDetailsMap) => {
  const productId = getProductIdForStockKey(row);
  const warehouseId = getWarehouseIdForStockKey(row);
  const title = getProductTitleForStockKey(row);

  if (warehouseId && productId) {
    const exact = productDetailsMap?.byWarehouseProductId?.get(
      `${warehouseId}::${productId}`,
    );
    if (exact) return exact;
  }

  if (productId) {
    const byId = productDetailsMap?.byId?.get(productId);
    if (byId) return byId;
  }

  if (warehouseId && title) {
    const exactTitle = productDetailsMap?.byWarehouseTitle?.get(
      `${warehouseId}::${title}`,
    );
    if (exactTitle) return exactTitle;
  }

  if (title) return productDetailsMap?.byTitle?.get(title) || null;
  return null;
};

const mergeProductDetailsIntoStockRow = (row, productDetailsMap) => {
  const productDetails = findProductDetailsForStockRow(row, productDetailsMap);
  if (!productDetails) return row;

  return {
    ...row,
    _productDetailsFromProductList: productDetails,
    _productIdResolved: normId(
      productDetails?._id || productDetails?.id || row?.productId,
    ),
    productId: row?.productId || productDetails?._id,
    Product_Title:
      row?.Product_Title ||
      row?.productId?.Product_Title ||
      productDetails?.Product_Title ||
      productDetails?.productName ||
      "",
    HSN_Code:
      row?.HSN_Code ||
      row?.productId?.HSN_Code ||
      productDetails?.HSN_Code ||
      productDetails?.hsn ||
      "",
    openingFinancialYear: firstDefined(
      productDetails?.openingFinancialYear,
      productDetails?.OpeningFinancialYear,
      productDetails?.financialYearOpeningYear,
      productDetails?.openingFY,
      productDetails?.OpeningFY,
      productDetails?.openingYear,
      row?.openingFinancialYear,
      row?.OpeningFinancialYear,
    ),
    Opening_Stock: firstDefined(
      productDetails?.Opening_Stock,
      productDetails?.openingStock,
      productDetails?.openingQty,
      row?.Opening_Stock,
      row?.openingStock,
      row?.oQty,
    ),
    openingRate: firstDefined(
      productDetails?.openingRate,
      productDetails?.Opening_Rate,
      productDetails?.OpeningRate,
      productDetails?.oRate,
      productDetails?.purchaseRate,
      productDetails?.PurchaseRate,
      productDetails?.Product_Purchase_Rate,
      productDetails?.Purchase_Price,
      productDetails?.purchasePrice,
      productDetails?.PurchasePrice,
      productDetails?.costPrice,
      productDetails?.Product_Rate,
      row?.openingRate,
      row?.oRate,
    ),
  };
};

const loadPreviousSnapshotOpening = (database, financialYear, row) => {
  try {
    const previousFY = getPreviousFY(financialYear);
    const raw = localStorage.getItem(stockSnapshotKey(database, previousFY));
    const snapshot = raw ? JSON.parse(raw) : {};
    const item = findPreviousClosingForRow(row, snapshot);

    if (!item) return null;
    return {
      qty: num(item?.closingQty),
      rate: num(item?.closingAveRate),
    };
  } catch {
    return null;
  }
};

const getOpeningForFinancialYear = (
  row,
  financialYear,
  database,
  previousClosingMap = {},
) => {
  const openingFY = getOpeningFinancialYear(row);
  const selectedStart = parseFYStart(financialYear);
  const openingStart = openingFY ? parseFYStart(openingFY) : null;
  const savedOpening = getProductSavedOpening(row);

  if (openingFY && openingStart > selectedStart) return { qty: 0, rate: 0 };

  if (openingFY && openingStart === selectedStart) {
    return { qty: num(savedOpening?.qty), rate: num(savedOpening?.rate) };
  }

  if (openingFY && openingStart < selectedStart) {
    const previousFromApi = findPreviousClosingForRow(row, previousClosingMap);
    if (previousFromApi) {
      return {
        qty: num(previousFromApi?.closingQty),
        rate: num(previousFromApi?.closingAveRate),
      };
    }

    const previousSnapshot = loadPreviousSnapshotOpening(
      database,
      financialYear,
      row,
    );
    return previousSnapshot || { qty: 0, rate: 0 };
  }

  const backendOpening = extractBackendFYOpening(row, financialYear);
  if (backendOpening) return backendOpening;

  if (savedOpening?.qty || savedOpening?.rate) return savedOpening;

  return {
    qty: num(row?.oQty ?? row?.openingQty ?? row?.Opening_Stock),
    rate: num(row?.openingRate ?? row?.oRate),
  };
};

const normalizeClosingRowsFromResponse = (response) => {
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.closingStock)) return response.closingStock;
  if (Array.isArray(response?.Stock)) return response.Stock;
  if (Array.isArray(response?.stock)) return response.stock;
  if (!Array.isArray(response?.Warehouse)) return [];

  const rows = [];
  response.Warehouse.forEach((warehouse) => {
    if (!Array.isArray(warehouse?.productItems)) {
      rows.push(warehouse);
      return;
    }

    warehouse.productItems.forEach((item, index) => {
      const productObject =
        item?.productId && typeof item.productId === "object"
          ? item.productId
          : {};

      rows.push({
        ...item,
        _id:
          item?._id || `${warehouse?._id || warehouse?.warehouseId}-${index}`,
        warehouseId:
          warehouse?.warehouseId || warehouse?._id || warehouse?.warehouseId1,
        warehouseName: warehouse?.warehouseName || "",
        Product_Title:
          item?.Product_Title ||
          productObject?.Product_Title ||
          item?.productName ||
          "",
        HSN_Code:
          item?.HSN_Code || productObject?.HSN_Code || productObject?.hsn || "",
        productId: item?.productId,
        date:
          warehouse?.date ||
          item?.date ||
          warehouse?.createdAt ||
          item?.createdAt,
        oQty: firstDefined(item?.oQty, item?.openingQty, item?.Opening_Stock),
        openingRate: firstDefined(
          item?.openingRate,
          item?.oRate,
          item?.price,
          item?.cRate,
        ),
      });
    });
  });

  return rows;
};

const getInvoiceItemProductId = (item = {}) =>
  normId(
    item?.productId?._id ||
      item?.productId?.id ||
      item?.productId ||
      item?.ProductId ||
      item?.product_id ||
      item?.product?._id ||
      "",
  );

const getInvoiceItemTitle = (item = {}) =>
  String(
    item?.productId?.Product_Title ||
      item?.productData?.Product_Title ||
      item?.product?.Product_Title ||
      item?.Product_Title ||
      item?.productName ||
      item?.ProductName ||
      "",
  )
    .trim()
    .toLowerCase();

const getInvoiceItemWarehouseId = (item = {}) =>
  normId(
    item?.warehouse?._id ||
      item?.warehouse ||
      item?.warehouseId?._id ||
      item?.warehouseId ||
      item?.WarehouseId ||
      item?.productData?.warehouse?._id ||
      item?.productData?.warehouse ||
      item?.productId?.warehouse?._id ||
      item?.productId?.warehouse ||
      "",
  );

const buildSalesStockMap = (invoices = [], statusFilter) => {
  const bucket = {};

  const add = (key, qty, price) => {
    if (!key || qty <= 0) return;
    if (!bucket[key]) bucket[key] = { qty: 0, amount: 0 };
    bucket[key].qty += qty;
    bucket[key].amount += qty * price;
  };

  (Array.isArray(invoices) ? invoices : []).forEach((invoice) => {
    const status = String(invoice?.status || "")
      .trim()
      .toLowerCase();
    if (statusFilter && !statusFilter(status)) return;

    (Array.isArray(invoice?.orderItems) ? invoice.orderItems : []).forEach(
      (item) => {
        const qty = num(
          item?.qty ??
            item?.quantity ??
            item?.Qty ??
            item?.productQty ??
            item?.saleQty,
        );
        const price = num(
          item?.price ??
            item?.rate ??
            item?.salesRate ??
            item?.saleRate ??
            item?.productId?.price ??
            item?.productId?.salesRate ??
            item?.productId?.saleRate,
        );
        const productId = getInvoiceItemProductId(item);
        const title = getInvoiceItemTitle(item);
        const warehouseId = getInvoiceItemWarehouseId(item);

        if (warehouseId && productId) {
          add(`${warehouseId}::${productId}`, qty, price);
        }
        if (warehouseId && title) {
          add(`${warehouseId}::title:${title}`, qty, price);
        }
        if (productId) add(`id:${productId}`, qty, price);
        if (title) add(`title:${title}`, qty, price);
      },
    );
  });

  return Object.fromEntries(
    Object.entries(bucket).map(([key, value]) => [
      key,
      {
        qty: round2(value.qty),
        rate: value.qty > 0 ? round2(value.amount / value.qty) : 0,
        total: round2(value.amount),
      },
    ]),
  );
};

const buildInwardStockMap = (purchaseInvoices = []) => {
  const bucket = {};

  const add = (key, qty, rate) => {
    if (!key || qty <= 0) return;
    if (!bucket[key]) {
      bucket[key] = { qty: 0, rateSum: 0, rateCount: 0 };
    }
    bucket[key].qty += qty;
    if (rate > 0) {
      bucket[key].rateSum += rate;
      bucket[key].rateCount += 1;
    }
  };

  (Array.isArray(purchaseInvoices) ? purchaseInvoices : []).forEach(
    (invoice) => {
      const status = String(invoice?.status || "")
        .trim()
        .toLowerCase();
      if (!status.includes("completed")) return;

      (Array.isArray(invoice?.orderItems) ? invoice.orderItems : []).forEach(
        (item) => {
          const qty = num(
            item?.qty ??
              item?.quantity ??
              item?.Qty ??
              item?.productQty ??
              item?.pQty,
          );
          const rate = num(
            item?.price ??
              item?.basicPrice ??
              item?.rate ??
              item?.purchaseRate ??
              item?.PurchaseRate ??
              item?.productData?.purchaseRate ??
              item?.productData?.PurchaseRate ??
              item?.productData?.price ??
              item?.productId?.purchaseRate ??
              item?.productId?.PurchaseRate ??
              item?.productId?.price,
          );
          const productId = getInvoiceItemProductId(item);
          const title = getInvoiceItemTitle(item);
          const warehouseId = getInvoiceItemWarehouseId(item);

          if (warehouseId && productId) {
            add(`${warehouseId}::${productId}`, qty, rate);
          }
          if (warehouseId && title) {
            add(`${warehouseId}::title:${title}`, qty, rate);
          }
          if (productId) add(`id:${productId}`, qty, rate);
          if (title) add(`title:${title}`, qty, rate);
        },
      );
    },
  );

  return Object.fromEntries(
    Object.entries(bucket).map(([key, value]) => {
      const averageRate =
        value.rateCount > 0 ? value.rateSum / value.rateCount : 0;
      return [
        key,
        {
          qty: round2(value.qty),
          rate: round2(averageRate),
          total: round2(value.qty * averageRate),
        },
      ];
    }),
  );
};

const findStockMovementForRow = (row, stockMap = {}) => {
  const productId = getProductIdForStockKey(row);
  const productTitle = getProductTitleForStockKey(row);
  const warehouseId = getWarehouseIdForStockKey(row);

  const keys = [];

  if (warehouseId && productId) {
    keys.push(`${warehouseId}::${productId}`);
  }
  if (warehouseId && productTitle) {
    keys.push(`${warehouseId}::title:${productTitle}`);
  }
  if (productId) keys.push(`id:${productId}`);
  if (productTitle) keys.push(`title:${productTitle}`);

  for (const key of keys) {
    if (stockMap?.[key]) return stockMap[key];
  }

  return { qty: 0, rate: 0, total: 0 };
};

const pendingStockStatuses = [
  "pending",
  "billing",
  "dispatch",
  "delivery boy assigned",
  "out for delivery",
];

const isPendingStockStatus = (status) => {
  const normalized = String(status || "")
    .trim()
    .toLowerCase();
  if (
    !normalized ||
    normalized.includes("cancel") ||
    normalized.includes("completed")
  ) {
    return false;
  }
  return pendingStockStatuses.some((code) => normalized.includes(code));
};

const buildResolvedClosing = (row, opening, outward) => {
  const openingQty = num(opening?.qty);
  const openingRate = num(opening?.rate);
  const openingTotal = openingQty * openingRate;
  const inwardQty = num(row?.pQty);
  const inwardRate = num(row?.avgPurchaseRate);
  const inwardTotal = inwardQty * inwardRate;
  const pendingQty = num(row?.pendingStock);
  const outwardQty = num(outward?.qty);

  const closingQty = round2(openingQty + inwardQty - pendingQty - outwardQty);
  const availableQty = openingQty + inwardQty;
  const availableValue = openingTotal + inwardTotal;
  const closingAveRate =
    availableQty > 0 ? round2(availableValue / availableQty) : 0;

  return {
    closingQty,
    closingAveRate,
    closingTotal: round2(closingQty * closingAveRate),
  };
};

const resolveWarehouseForStockRow = (row, warehouseMap) => {
  const warehouseId = normId(
    row?.warehouseId ??
      row?.warehouse?._id ??
      row?.warehouse ??
      row?.warehouseId1 ??
      row?.productId?.warehouse?._id ??
      row?.productId?.warehouse ??
      row?.product?.warehouse?._id ??
      row?.product?.warehouse ??
      "",
  );

  return {
    ...row,
    _warehouseIdResolved: warehouseId,
    _warehouseNameResolved:
      row?.warehouseName || warehouseMap.get(warehouseId) || "",
  };
};

const fetchFinancialYearMovements = async (database, financialYear, userId) => {
  const selectedDatabase = buildSelectedDatabase(database, financialYear);

  const [salesResult, purchaseResult] = await Promise.allSettled([
    createOrderhistoryviewDATABASE(selectedDatabase),
    PurchaseOrderList(userId, selectedDatabase),
  ]);

  const salesResponse =
    salesResult.status === "fulfilled" ? salesResult.value : {};
  const purchaseResponse =
    purchaseResult.status === "fulfilled" ? purchaseResult.value : {};

  const salesInvoices = [
    ...(Array.isArray(salesResponse?.b2bData) ? salesResponse.b2bData : []),
    ...(Array.isArray(salesResponse?.b2csData) ? salesResponse.b2csData : []),
    ...(Array.isArray(salesResponse?.b2clData) ? salesResponse.b2clData : []),
    ...(Array.isArray(salesResponse?.orderHistory)
      ? salesResponse.orderHistory
      : []),
  ];
  const purchaseInvoices = Array.isArray(purchaseResponse?.orderHistory)
    ? purchaseResponse.orderHistory
    : [];

  return {
    inwardMap: buildInwardStockMap(purchaseInvoices),
    pendingMap: buildSalesStockMap(salesInvoices, isPendingStockStatus),
    outwardMap: buildSalesStockMap(
      salesInvoices,
      (status) =>
        String(status || "")
          .trim()
          .toLowerCase() === "completed",
    ),
  };
};

const loadPreviousClosingMapForOrder = async ({
  database,
  financialYear,
  userId,
  warehouseMap,
  productDetailsMap,
  depth = 0,
}) => {
  if (depth > 8) return {};

  try {
    const previousFY = getPreviousFY(financialYear);
    const response = await _Get(
      WareHouse_Closing_Stock,
      `${database}?financialYear=${encodeURIComponent(previousFY)}`,
    ).catch((error) => {
      silentApiError(error);
      return { Warehouse: [] };
    });
    const apiRows = normalizeClosingRowsFromResponse(response).map((row) =>
      mergeProductDetailsIntoStockRow(
        resolveWarehouseForStockRow(row, warehouseMap),
        productDetailsMap,
      ),
    );

    const existingKeys = new Set(
      apiRows.flatMap((row) => getStockLookupKeys(row)),
    );

    const missingProductRows = (
      Array.isArray(productDetailsMap?.list) ? productDetailsMap.list : []
    )
      .filter((item) => {
        const keys = getStockLookupKeys(item);
        return keys.length && !keys.some((key) => existingKeys.has(key));
      })
      .map((item) =>
        mergeProductDetailsIntoStockRow(
          resolveWarehouseForStockRow(
            {
              ...item,
              productId: item?._id,
              Product_Title: getProductTitleForStockKey(item),
              HSN_Code: item?.HSN_Code || item?.hsn || "",
              warehouseId:
                item?.warehouse?._id ||
                item?.warehouse ||
                item?.warehouseId ||
                "",
            },
            warehouseMap,
          ),
          productDetailsMap,
        ),
      );

    const rawRows = [...apiRows, ...missingProductRows];

    const previousStart = parseFYStart(previousFY);
    const needsOlderFY = rawRows.some((row) => {
      const openingFY = getOpeningFinancialYear(row);
      return openingFY && parseFYStart(openingFY) < previousStart;
    });

    const olderClosingMap = needsOlderFY
      ? await loadPreviousClosingMapForOrder({
          database,
          financialYear: previousFY,
          userId,
          warehouseMap,
          productDetailsMap,
          depth: depth + 1,
        })
      : {};

    const { inwardMap, pendingMap, outwardMap } =
      await fetchFinancialYearMovements(database, previousFY, userId);
    const closingMap = {};

    rawRows.forEach((row) => {
      const opening = getOpeningForFinancialYear(
        row,
        previousFY,
        database,
        olderClosingMap,
      );
      const inward = findStockMovementForRow(row, inwardMap);
      const pending = findStockMovementForRow(row, pendingMap);
      const outward = findStockMovementForRow(row, outwardMap);
      const resolvedRow = {
        ...row,
        pQty: num(inward?.qty),
        avgPurchaseRate: num(inward?.rate),
        pendingStock: num(pending?.qty),
      };

      addPreviousClosingToMap(
        closingMap,
        row,
        buildResolvedClosing(resolvedRow, opening, outward),
      );
    });

    return closingMap;
  } catch (error) {
    silentApiError(error);
    return {};
  }
};

const loadResolvedClosingRowsForOrder = async ({
  database,
  financialYear,
  userId,
  products,
}) => {
  const [stockResponse, warehouseResponse] = await Promise.all([
    _Get(
      WareHouse_Closing_Stock,
      `${database}?financialYear=${encodeURIComponent(financialYear)}`,
    ).catch((error) => {
      silentApiError(error);
      return { Warehouse: [] };
    }),
    _Get(Create_Warehouse_List, database).catch(() => ({ Warehouse: [] })),
  ]);

  const warehouses = Array.isArray(warehouseResponse?.Warehouse)
    ? warehouseResponse.Warehouse
    : [];
  const warehouseMap = new Map(
    warehouses.map((warehouse) => [
      String(warehouse?._id || ""),
      warehouse?.warehouseName || "",
    ]),
  );
  const productDetailsMap = buildProductDetailsMap(products);

  const apiRows = normalizeClosingRowsFromResponse(stockResponse).map((row) =>
    mergeProductDetailsIntoStockRow(
      resolveWarehouseForStockRow(row, warehouseMap),
      productDetailsMap,
    ),
  );

  // Some backends omit products that have no movement in the selected FY.
  // Add those products from the master so their saved opening stock or
  // previous-FY closing stock is still calculated and displayed correctly.
  const existingKeys = new Set(
    apiRows.flatMap((row) => getStockLookupKeys(row)),
  );

  const missingProductRows = (Array.isArray(products) ? products : [])
    .filter((item) => {
      const keys = getStockLookupKeys(item);
      return keys.length && !keys.some((key) => existingKeys.has(key));
    })
    .map((item) =>
      mergeProductDetailsIntoStockRow(
        resolveWarehouseForStockRow(
          {
            ...item,
            productId: item?._id,
            Product_Title: getProductTitleForStockKey(item),
            HSN_Code: item?.HSN_Code || item?.hsn || "",
            warehouseId:
              item?.warehouse?._id ||
              item?.warehouse ||
              item?.warehouseId ||
              "",
          },
          warehouseMap,
        ),
        productDetailsMap,
      ),
    );

  const rawRows = [...apiRows, ...missingProductRows];

  const previousClosingMap = await loadPreviousClosingMapForOrder({
    database,
    financialYear,
    userId,
    warehouseMap,
    productDetailsMap,
  });
  const { inwardMap, pendingMap, outwardMap } =
    await fetchFinancialYearMovements(database, financialYear, userId);

  return rawRows
    .map((row) => {
      const opening = getOpeningForFinancialYear(
        row,
        financialYear,
        database,
        previousClosingMap,
      );
      const inward = findStockMovementForRow(row, inwardMap);
      const pending = findStockMovementForRow(row, pendingMap);
      const outward = findStockMovementForRow(row, outwardMap);
      const rowWithMovements = {
        ...row,
        pQty: num(inward?.qty),
        avgPurchaseRate: num(inward?.rate),
        pendingStock: num(pending?.qty),
      };
      const closing = buildResolvedClosing(rowWithMovements, opening, outward);

      return {
        ...rowWithMovements,
        sQty: num(outward?.qty),
        closingQty: closing.closingQty,
        closingAveRate: closing.closingAveRate,
        closingTotal: closing.closingTotal,
      };
    })
    .filter(
      (row) =>
        !row?._isSummary &&
        String(row?.warehouseName || "").toLowerCase() !== "total summary" &&
        String(
          row?.Product_Title || row?.productId?.Product_Title || "",
        ).toLowerCase() !== "total",
    );
};

const buildClosingStockQtyMap = (rows = []) => {
  const map = {};

  const add = (key, qty, warehouse, shouldAggregate) => {
    if (!key) return;
    if (!map[key] || !shouldAggregate) {
      map[key] = { qty: num(qty), warehouse: warehouse || "" };
      return;
    }

    map[key] = {
      qty: round2(num(map[key]?.qty) + num(qty)),
      warehouse: map[key]?.warehouse || warehouse || "",
    };
  };

  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const productId = getProductIdForStockKey(row);
    const productTitle = getProductTitleForStockKey(row);
    const warehouseId = getWarehouseIdForStockKey(row);
    const closingQty = num(row?.closingQty ?? row?.currentStock);

    if (warehouseId && productId)
      add(`${warehouseId}::${productId}`, closingQty, warehouseId, true);
    if (warehouseId && productTitle)
      add(
        `${warehouseId}::title:${productTitle}`,
        closingQty,
        warehouseId,
        true,
      );
    if (productId) add(`id:${productId}`, closingQty, warehouseId, true);
    if (productTitle)
      add(`title:${productTitle}`, closingQty, warehouseId, true);
  });

  return map;
};

const getClosingStockLookup = (selectedItem, closingStockMap = {}) => {
  const productId = normId(selectedItem?._id || selectedItem?.productId?._id);
  const productTitle = getProductTitleForStockKey(selectedItem);
  const productWarehouseId = normId(
    selectedItem?.warehouse?._id ||
      selectedItem?.warehouse ||
      selectedItem?.warehouseId?._id ||
      selectedItem?.warehouseId,
  );

  const keys = [];
  if (productWarehouseId && productId)
    keys.push(`${productWarehouseId}::${productId}`);
  if (productWarehouseId && productTitle)
    keys.push(`${productWarehouseId}::title:${productTitle}`);
  if (productId) keys.push(`id:${productId}`);
  if (productTitle) keys.push(`title:${productTitle}`);

  for (const key of keys) {
    if (!Object.prototype.hasOwnProperty.call(closingStockMap, key)) continue;
    const item = closingStockMap[key];
    return {
      qty: Math.max(0, num(item?.qty ?? item)),
      warehouse: productWarehouseId || item?.warehouse || "",
    };
  }

  return { qty: 0, warehouse: productWarehouseId || "" };
};

const arrFromApi = (res, keys = []) => {
  for (const key of keys) {
    if (Array.isArray(res?.[key])) return res[key];
    if (Array.isArray(res?.data?.[key])) return res.data[key];
  }

  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;

  return [];
};

const getEmail = (p) =>
  p?.email || p?.Email || p?.emailId || p?.EmailId || p?.mail || "";

const getProductTitle = (p = {}) =>
  p?.Product_Title || p?.productName || p?.ProductName || p?.name || "Product";

const getPurchaseRate = (p = {}) =>
  toNum(
    p?.purchaseRate ??
      p?.PurchaseRate ??
      p?.lastPurchaseRate ??
      p?.lastPrice ??
      p?.Product_Purchase_Rate ??
      p?.Purchase_Price ??
      p?.purchasePrice ??
      p?.PurchasePrice ??
      p?.costPrice ??
      p?.openingRate ??
      p?.Opening_Rate ??
      p?.price ??
      0,
  );

const getPurchasePriceIncludingGST = (product = {}, gstPercentage = 0) => {
  const explicitInclusive = firstDefined(
    product?.purchasePriceWithGST,
    product?.PurchasePriceWithGST,
    product?.purchasePriceInclGST,
    product?.PurchasePriceInclGST,
    product?.purchaseRateWithGST,
    product?.PurchaseRateWithGST,
  );

  if (explicitInclusive !== undefined) {
    return round2(toNum(explicitInclusive, 0));
  }

  const basicPrice = getPurchaseRate(product);
  return round2(basicPrice * (1 + parseGstPct(gstPercentage) / 100));
};

const basicFromInclusivePurchasePrice = (inclusivePrice, gstPercentage) => {
  const divisor = 1 + parseGstPct(gstPercentage) / 100;
  return divisor > 0
    ? round2(toNum(inclusivePrice, 0) / divisor)
    : round2(toNum(inclusivePrice, 0));
};

const emptyProductRow = () => ({
  productId: "",
  productData: "",
  warehouse: "",
  warehouseName: "",
  availableQty: 0,
  wholeQty: 0,
  stockLoading: false,
  HSN_Code: "",
  primaryUnit: "",
  secondaryUnit: "",
  secondarySize: 1,
  qty: 1,
  GrossQty: 0,
  gstPercentage: 0,
  mrp: 0,
  purchasePriceWithGst: 0,
  basicPrice: 0,
  purchaseRate: 0,
  taxableAmount: 0,
  sgstRate: 0,
  cgstRate: 0,
  igstRate: 0,
  grandTotal: 0,
  disCountPercentage: 0,
});

const sumLineBasics = (rows = []) =>
  rows.reduce(
    (sum, r) =>
      sum + Number(r?.qty || 0) * Number(r?.basicPrice || r?.price || 0),
    0,
  );

const computeAdjustments = (base = 0, discountRows = [], chargeRows = []) => {
  let running = Math.max(0, round2(base));

  const discApplied = [];
  const chgApplied = [];

  (discountRows || []).forEach((sourceRow) => {
    const row = { ...sourceRow };
    const method = String(row?.discounttype || "")
      .trim()
      .toLowerCase();

    let applied = 0;
    if (method === "percentage") {
      applied = round2(
        (running * Math.max(0, toNum(row?.percentage, 0))) / 100,
      );
    } else {
      applied = round2(Math.max(0, toNum(row?.amount, 0)));
    }

    // A discount cannot reduce the running taxable value below zero.
    row.__applied = round2(Math.min(applied, running));
    running = round2(Math.max(0, running - row.__applied));
    discApplied.push(row);
  });

  (chargeRows || []).forEach((sourceRow) => {
    const row = { ...sourceRow };
    const method = String(row?.discounttype || "")
      .trim()
      .toLowerCase();

    if (method === "percentage") {
      row.__applied = round2(
        (running * Math.max(0, toNum(row?.percentage, 0))) / 100,
      );
    } else {
      row.__applied = round2(Math.max(0, toNum(row?.amount, 0)));
    }

    running = round2(running + row.__applied);
    chgApplied.push(row);
  });

  const discountTotal = round2(
    discApplied.reduce((sum, row) => sum + toNum(row?.__applied, 0), 0),
  );
  const chargesTotal = round2(
    chgApplied.reduce((sum, row) => sum + toNum(row?.__applied, 0), 0),
  );

  return {
    discountTotal,
    chargesTotal,
    discountRowsApplied: discApplied,
    chargeRowsApplied: chgApplied,
    taxableItemsAfterDiscount: round2(
      Math.max(0, Number(base || 0) - discountTotal),
    ),
    netAfterAdj: round2(running),
  };
};

const buildDiscountDetailsPayload = (discRowsApplied, chgRowsApplied) => {
  const mapRow = (x, asType) => {
    const isPct = String(x?.discounttype || "").toLowerCase() === "percentage";
    const percentage = isPct ? Number(x?.percentage || 0) : 0;
    const appliedAmount = round2(Number(x?.__applied || 0));

    return {
      title: x?.title || asType,
      type: asType,
      discounttype: x?.discounttype || "",
      disType: isPct ? "Percentage" : "Amount",
      percentage: percentage || 0,
      amount: appliedAmount,
      discountedAmount: appliedAmount,
      discountedValue: isPct ? percentage : appliedAmount,
    };
  };

  const cleanedDiscounts = (discRowsApplied || [])
    .filter((x) => Number(x?.__applied || 0) > 0)
    .map((x) => mapRow(x, "Discount"));

  const cleanedCharges = (chgRowsApplied || [])
    .filter((x) => Number(x?.__applied || 0) > 0)
    .map((x) => mapRow(x, "Charges"));

  return [...cleanedDiscounts, ...cleanedCharges];
};

const calculatePurchaseTotals = (
  party,
  items = [],
  discountRows = [],
  chargeRows = [],
  companyDetails = {},
) => {
  const regionRaw = String(
    party?.regionType ||
      party?.RegionType ||
      party?.region ||
      party?.taxRegion ||
      "",
  )
    .trim()
    .toLowerCase();

  const explicitOutside =
    regionRaw.includes("outside") ||
    regionRaw.includes("interstate") ||
    regionRaw.includes("inter-state") ||
    regionRaw === "igst";

  const explicitInside =
    regionRaw.includes("inside") ||
    regionRaw.includes("within") ||
    regionRaw.includes("intrastate") ||
    regionRaw.includes("intra-state");

  const partyState = String(
    party?.State || party?.state || party?.stateName || "",
  )
    .trim()
    .toLowerCase();

  const companyState = String(
    companyDetails?.State ||
      companyDetails?.state ||
      companyDetails?.companyState ||
      "",
  )
    .trim()
    .toLowerCase();

  const isIGST =
    explicitOutside ||
    (!explicitInside &&
      !!partyState &&
      !!companyState &&
      partyState !== companyState);

  const basicTotal = round2(sumLineBasics(items));
  const adjustments = computeAdjustments(basicTotal, discountRows, chargeRows);

  const taxableItems = round2(
    Math.max(0, adjustments?.taxableItemsAfterDiscount || 0),
  );
  const itemDiscountRatio =
    basicTotal > 0 ? Math.min(1, taxableItems / basicTotal) : 0;

  let maxGst = 0;
  let itemSgstTotal = 0;
  let itemCgstTotal = 0;
  let itemIgstTotal = 0;

  const rawItemRows = (items || []).map((item) => {
    const qty = Math.max(0, toNum(item?.qty, 0));
    const basicPrice = Math.max(0, toNum(item?.basicPrice ?? item?.price, 0));

    return {
      item,
      qty,
      basicPrice,
      rawTaxable: round2(qty * basicPrice),
    };
  });

  const lastTaxableIndex = rawItemRows.reduce(
    (lastIndex, row, index) => (row.rawTaxable > 0 ? index : lastIndex),
    -1,
  );

  let allocatedTaxable = 0;

  const gstDetails = rawItemRows.map(({ item, rawTaxable }, index) => {
    let taxable = 0;

    if (rawTaxable > 0) {
      if (index === lastTaxableIndex) {
        taxable = round2(Math.max(0, taxableItems - allocatedTaxable));
      } else {
        taxable = round2(rawTaxable * itemDiscountRatio);
        allocatedTaxable = round2(allocatedTaxable + taxable);
      }
    }

    const gstPercentage = Math.max(
      0,
      parseGstPct(item?.gstPercentage ?? item?.GSTRate ?? 0),
    );

    maxGst = Math.max(maxGst, gstPercentage);

    let sgstRate = 0;
    let cgstRate = 0;
    let igstRate = 0;

    const itemTax = round2((taxable * gstPercentage) / 100);

    if (isIGST) {
      igstRate = itemTax;
      itemIgstTotal = round2(itemIgstTotal + itemTax);
    } else {
      sgstRate = round2(itemTax / 2);
      cgstRate = round2(itemTax - sgstRate);
      itemSgstTotal = round2(itemSgstTotal + sgstRate);
      itemCgstTotal = round2(itemCgstTotal + cgstRate);
    }

    return {
      hsn: item?.HSN_Code || item?.productData?.HSN_Code || "NA",
      productId: item?.productId || item?.productData?._id || "",
      rawTaxable,
      taxable,
      gstPercentage,
      discountPercentage: toNum(item?.disCountPercentage, 0),
      sgstRate,
      cgstRate,
      igstRate,
      grandTotal: round2(taxable + sgstRate + cgstRate + igstRate),
    };
  });

  const chargesTotal = round2(adjustments?.chargesTotal || 0);
  const taxOnCharges =
    chargesTotal > 0 && maxGst > 0 ? round2((chargesTotal * maxGst) / 100) : 0;

  let ChargesSgst = 0;
  let ChargesCgst = 0;
  let ChargesIgst = 0;

  if (isIGST) {
    ChargesIgst = taxOnCharges;
  } else {
    ChargesSgst = round2(taxOnCharges / 2);
    ChargesCgst = round2(taxOnCharges - ChargesSgst);
  }

  const SgstTotal = round2(itemSgstTotal + ChargesSgst);
  const CgstTotal = round2(itemCgstTotal + ChargesCgst);
  const IgstTotal = round2(itemIgstTotal + ChargesIgst);

  const taxableValue = round2(taxableItems + chargesTotal);
  const grossBeforeRound = round2(
    taxableValue + SgstTotal + CgstTotal + IgstTotal,
  );
  const GrandTotal = roundHalfUp(grossBeforeRound);
  const RoundOffSigned = round2(GrandTotal - grossBeforeRound);
  const RoundOff = round2(Math.abs(RoundOffSigned));

  return {
    Tax: {
      BasicTotal: basicTotal,
      DiscountTotal: round2(adjustments?.discountTotal || 0),
      ChargesTotal: chargesTotal,
      TaxableItems: taxableItems,
      Amount: taxableValue,
      SgstTotal,
      CgstTotal,
      IgstTotal,
      ChargesSgst,
      ChargesCgst,
      ChargesIgst,
      RoundOff,
      RoundOffSigned,
      GrandTotal,
      GrossBeforeRound: grossBeforeRound,
      IgstTaxType: isIGST ? 1 : 0,
      maxGst,
    },
    gstDetails,
    adjustments,
  };
};

const buildHsnSummary = (gstData, chargeRowsApplied = []) => {
  const details = Array.isArray(gstData?.gstDetails) ? gstData.gstDetails : [];
  const isIGST = !!gstData?.Tax?.IgstTaxType;

  const byHSN = new Map();

  details.forEach((r) => {
    const hsn = String(r?.hsn || "NA");
    const ratePct = parseGstPct(r?.gstPercentage || 0);
    const groupKey = `${hsn}::${ratePct}`;

    const g = byHSN.get(groupKey) || {
      hsn,
      ratePct,
      taxable: 0,
      cgst: 0,
      sgst: 0,
      igst: 0,
    };

    g.taxable = round2(g.taxable + Number(r?.taxable || 0));
    g.cgst = round2(g.cgst + Number(r?.cgstRate || 0));
    g.sgst = round2(g.sgst + Number(r?.sgstRate || 0));
    g.igst = round2(g.igst + Number(r?.igstRate || 0));

    byHSN.set(groupKey, g);
  });

  let maxRate = 0;
  for (const v of byHSN.values()) {
    maxRate = Math.max(maxRate, v.ratePct);
  }

  const chargesAmt = (chargeRowsApplied || []).reduce(
    (s, x) => s + (Number(x?.__applied) || 0),
    0,
  );

  const chargesTax = maxRate > 0 ? (chargesAmt * maxRate) / 100 : 0;

  const rows = Array.from(byHSN.values())
    .map((r) => ({
      kind: "ITEM",
      hsn: r.hsn,
      ratePct: r.ratePct,
      taxable: round2(r.taxable),
      cgst: round2(r.cgst),
      sgst: round2(r.sgst),
      igst: round2(r.igst),
      total: round2(r.taxable + r.cgst + r.sgst + r.igst),
    }))
    .sort((a, b) => b.ratePct - a.ratePct);

  if (chargesAmt > 0) {
    rows.push({
      kind: "CHARGE",
      hsn: `CHARGES @ ${maxRate}%`,
      ratePct: maxRate,
      taxable: round2(chargesAmt),
      cgst: isIGST ? 0 : round2(chargesTax / 2),
      sgst: isIGST ? 0 : round2(chargesTax / 2),
      igst: isIGST ? round2(chargesTax) : 0,
      total: round2(chargesAmt + chargesTax),
    });
  }

  const totals = rows.reduce(
    (acc, r) => ({
      taxable: acc.taxable + Number(r.taxable || 0),
      cgst: acc.cgst + Number(r.cgst || 0),
      sgst: acc.sgst + Number(r.sgst || 0),
      igst: acc.igst + Number(r.igst || 0),
      total: acc.total + Number(r.total || 0),
    }),
    {
      taxable: 0,
      cgst: 0,
      sgst: 0,
      igst: 0,
      total: 0,
    },
  );

  return {
    rows,
    totals,
    isIGST,
  };
};

// PO number comes directly from the total number of purchase orders returned
// by PurchaseOrderList for the selected financial-year database.
const buildNextPurchaseOrderNumber = (orders = []) => {
  const totalOrders = Array.isArray(orders) ? orders.length : 0;
  return String(totalOrders + 1).padStart(3, "0");
};

/* ---------------- component ---------------- */

const CreatePurchaseOrder = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const Context = useContext(UserContext);

  const [Loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  const [PartyLogin, setPartyLogin] = useState(false);

  const [PartyList, setPartyList] = useState([]);
  const [PartyId, setPartyId] = useState("");
  const [Party, setParty] = useState({});

  const [ProductList, setProductList] = useState([]);
  const [chargesMaster, setChargesMaster] = useState([]);

  const [OrderNumber, setOrderNumber] = useState("");
  const [orderDate, setOrderDate] = useState(todayStr());
  const [selectedDbText, setSelectedDbText] = useState("");

  const [closingStockQtyMap, setClosingStockQtyMap] = useState({});
  const [closingStockLoading, setClosingStockLoading] = useState(true);
  const [closingStockError, setClosingStockError] = useState("");

  const [showFYPrefixNotice, setShowFYPrefixNotice] = useState(false);
  const [currentFYLabel, setCurrentFYLabel] = useState("");
  const [draftReady, setDraftReady] = useState(false);

  const [product, setProduct] = useState([emptyProductRow()]);

  const [selectedChargesRows, setSelectedChargesRows] = useState([]);
  const [selectedDiscountRows, setSelectedDiscountRows] = useState([]);

  const multiselectRefs = useRef(product.map(() => React.createRef()));
  const partyMultiselectRef = useRef(null);
  const routePrefillAppliedRef = useRef(false);

  const styles = {
    headerRow: { margin: "0.5rem" },
    title: { margin: 0, fontSize: 22, fontWeight: 600 },
    right: { display: "flex", justifyContent: "flex-end" },
  };

  /* ---------------- only same multiselect chip css as CreateOrder ---------------- */

  useEffect(() => {
    const id = "multiselect-chip-fix";
    if (document.getElementById(id)) return;

    const s = document.createElement("style");
    s.id = id;
    s.textContent = `
      .multiselect-container .searchWrapper{
        min-height: 44px;
        padding: 6px 10px;
        border-radius: 10px;
        overflow: visible !important;
      }

      .multiselect-container .chip{
        display: inline-flex !important;
        align-items: center;
        gap: 8px;
        max-width: 100% !important;
        padding: 4px 8px !important;
        border-radius: 9999px !important;
        white-space: nowrap !important;
      }

      .multiselect-container .chip > span{
        white-space: nowrap !important;
        overflow: visible !important;
        text-overflow: unset !important;
      }

      .multiselect-container .optionContainer{
        z-index: 999999 !important;
        background: #fff !important;
      }
    `;
    document.head.appendChild(s);
  }, []);

  /* ---------------- logs ---------------- */

  useEffect(() => {
    console.log("STATE UPDATED - PARTY LIST:", PartyList);
    console.log("STATE UPDATED - PARTY LIST LENGTH:", PartyList.length);
  }, [PartyList]);

  useEffect(() => {
    console.log("STATE UPDATED - PRODUCT LIST:", ProductList);
    console.log("STATE UPDATED - PRODUCT LIST LENGTH:", ProductList.length);
  }, [ProductList]);

  /* ---------------- computed totals ---------------- */

  const gstData = useMemo(
    () =>
      calculatePurchaseTotals(
        Party,
        product,
        selectedDiscountRows,
        selectedChargesRows,
        Context?.CompanyDetails || {},
      ),
    [
      Party,
      product,
      selectedDiscountRows,
      selectedChargesRows,
      Context?.CompanyDetails,
    ],
  );

  const appliedDiscountRows = gstData?.adjustments?.discountRowsApplied || [];
  const appliedChargeRows = gstData?.adjustments?.chargeRowsApplied || [];

  const hsnSummary = useMemo(
    () => buildHsnSummary(gstData, appliedChargeRows),
    [gstData, appliedChargeRows],
  );

  /* ---------------- draft, stock and initial API load ---------------- */

  const getPurchaseDraftKey = (userData = {}) => {
    const financialYear =
      localStorage.getItem("financialYearSelected") ||
      getFinancialYearLabel(new Date());

    return `CREATE_PURCHASE_ORDER_DRAFT:${
      userData?.database || "default"
    }:${financialYear}`;
  };

  const hydrateProductRows = (sourceRows, products, stockMap) => {
    const rows =
      Array.isArray(sourceRows) && sourceRows.length
        ? sourceRows
        : [emptyProductRow()];

    return rows.map((sourceRow) => {
      const productId =
        sourceRow?.productId?._id ||
        sourceRow?.productId ||
        sourceRow?.productData?._id ||
        "";

      const productDetails =
        products.find((item) => String(item?._id) === String(productId)) ||
        sourceRow?.productData ||
        {};

      if (!productId && !productDetails?._id) {
        return {
          ...emptyProductRow(),
          ...sourceRow,
        };
      }

      const stock = getClosingStockLookup(productDetails, stockMap);
      const secondarySize =
        toNum(sourceRow?.secondarySize ?? productDetails?.secondarySize, 1) ||
        1;

      const qty = Math.max(0, toNum(sourceRow?.qty, 1));
      const gstPercentage = parseGstPct(
        firstDefined(sourceRow?.gstPercentage, productDetails?.GSTRate, 0),
      );

      const sourceInclusivePrice = firstDefined(
        sourceRow?.purchasePriceWithGst,
        sourceRow?.purchasePriceWithGST,
        sourceRow?.purchasePriceInclGST,
      );

      const basicPrice = Math.max(
        0,
        toNum(
          firstDefined(
            sourceRow?.basicPrice,
            sourceRow?.price,
            sourceRow?.lastPrice,
            sourceInclusivePrice !== undefined
              ? basicFromInclusivePurchasePrice(
                  sourceInclusivePrice,
                  gstPercentage,
                )
              : undefined,
            productDetails?.purchaseRate,
            productDetails?.PurchaseRate,
            getPurchaseRate(productDetails),
          ),
          0,
        ),
      );

      const purchasePriceWithGst = Math.max(
        0,
        toNum(
          firstDefined(
            sourceInclusivePrice,
            getPurchasePriceIncludingGST(productDetails, gstPercentage),
            round2(basicPrice * (1 + gstPercentage / 100)),
          ),
          0,
        ),
      );

      return {
        ...emptyProductRow(),
        ...sourceRow,
        productId: productDetails?._id || productId,
        productData: productDetails,
        warehouse:
          stock?.warehouse ||
          normId(
            sourceRow?.warehouse ||
              productDetails?.warehouse?._id ||
              productDetails?.warehouse ||
              productDetails?.warehouseId,
          ),
        warehouseName:
          sourceRow?.warehouseName ||
          productDetails?.warehouse?.warehouseName ||
          productDetails?.warehouseName ||
          "",
        availableQty: Math.max(0, toNum(stock?.qty, 0)),
        wholeQty: Math.max(0, toNum(stock?.qty, 0)),
        HSN_Code: sourceRow?.HSN_Code || productDetails?.HSN_Code || "",
        primaryUnit:
          sourceRow?.primaryUnit || productDetails?.primaryUnit || "",
        secondaryUnit:
          sourceRow?.secondaryUnit || productDetails?.secondaryUnit || "",
        secondarySize,
        qty,
        GrossQty: Number(
          toNum(
            sourceRow?.GrossQty,
            secondarySize > 0 ? qty / secondarySize : 0,
          ).toFixed(3),
        ),
        gstPercentage,
        mrp: toNum(sourceRow?.mrp ?? productDetails?.Product_MRP, 0),
        purchasePriceWithGst,
        basicPrice,
        purchaseRate: basicPrice,
        taxableAmount: round2(qty * basicPrice),
      };
    });
  };

  const loadPurchaseOrderHistory = async (userId, selectedDatabase) => {
    try {
      const response = await PurchaseOrderList(userId, selectedDatabase);
      return Array.isArray(response?.orderHistory) ? response.orderHistory : [];
    } catch (error) {
      silentApiError(error);
      return [];
    }
  };

  const refreshClosingStock = async ({
    userData,
    products = ProductList,
    updateExistingRows = true,
  }) => {
    if (!userData?.database) {
      setClosingStockQtyMap({});
      setClosingStockLoading(false);
      return {};
    }

    const financialYear =
      localStorage.getItem("financialYearSelected") ||
      getFinancialYearLabel(new Date());

    setClosingStockLoading(true);
    setClosingStockError("");

    try {
      const resolvedRows = await loadResolvedClosingRowsForOrder({
        database: userData.database,
        financialYear,
        userId: userData?._id,
        products,
      });

      const stockMap = buildClosingStockQtyMap(resolvedRows);
      setClosingStockQtyMap(stockMap);

      if (updateExistingRows) {
        setProduct((previousRows) =>
          hydrateProductRows(previousRows, products, stockMap),
        );
      }

      return stockMap;
    } catch (error) {
      silentApiError(error);
      setClosingStockQtyMap({});
      setClosingStockError(
        "Closing stock could not be loaded for the selected financial year.",
      );
      return {};
    } finally {
      setClosingStockLoading(false);
    }
  };

  useEffect(() => {
    let active = true;

    (async () => {
      setPageLoading(true);

      const userData = JSON.parse(localStorage.getItem("userData") || "{}");
      const selectedDatabase = getSelectedDatabase(userData);
      const selectedFinancialYear =
        localStorage.getItem("financialYearSelected") ||
        getFinancialYearLabel(new Date());

      setSelectedDbText(selectedDatabase);

      const isCustomer = userData?.rolename?.roleName === "Customer";
      setPartyLogin(!!isCustomer);

      let customerOwnerId = userData?._id;
      if (
        userData?.rolename?.roleName !== "SuperAdmin" &&
        userData?.created_by
      ) {
        customerOwnerId = userData?.created_by?._id || userData?.created_by;
      }

      const [productResult, partyResult, chargesResult, orderResult] =
        await Promise.allSettled([
          _Get(PurchaseProductList_Product, userData?.database),
          CreateCustomerList(customerOwnerId, userData?.database),
          _Get(ViewOther_Charges, userData?.database),
          PurchaseOrderList(userData?._id, selectedDatabase),
        ]);

      if (!active) return;

      const productResponse =
        productResult.status === "fulfilled" ? productResult.value : {};
      const rawProducts = arrFromApi(productResponse, [
        "Product",
        "Products",
        "product",
        "products",
      ]);

      const formattedProducts = rawProducts.map((item) => ({
        ...item,
        Product_Title: getProductTitle(item),
        value: item?._id,
        label: getProductTitle(item),
      }));
      setProductList(formattedProducts);

      const partyResponse =
        partyResult.status === "fulfilled" ? partyResult.value : {};
      const rawParties = arrFromApi(partyResponse, [
        "Customer",
        "Customers",
        "customer",
        "customers",
      ]);

      const formattedParties = rawParties.map((item) => {
        const name =
          item?.CompanyName ||
          item?.fullName ||
          item?.name ||
          item?.companyName ||
          "Party";

        return {
          ...item,
          CompanyName: name,
          displayLabel: `${name} (Grade: ${
            item?.category?.grade
              ? String(item.category.grade).toUpperCase()
              : "NA"
          })`,
        };
      });
      setPartyList(formattedParties);

      const chargesResponse =
        chargesResult.status === "fulfilled" ? chargesResult.value : {};
      const masterCharges = Array.isArray(chargesResponse?.OtherCharges)
        ? chargesResponse.OtherCharges
        : Array.isArray(chargesResponse?.data?.OtherCharges)
          ? chargesResponse.data.OtherCharges
          : [];
      setChargesMaster(masterCharges);

      const purchaseOrders =
        orderResult.status === "fulfilled" &&
        Array.isArray(orderResult.value?.orderHistory)
          ? orderResult.value.orderHistory
          : [];

      let stockMap = {};
      try {
        const resolvedRows = await loadResolvedClosingRowsForOrder({
          database: userData?.database,
          financialYear: selectedFinancialYear,
          userId: userData?._id,
          products: formattedProducts,
        });
        stockMap = buildClosingStockQtyMap(resolvedRows);
        setClosingStockQtyMap(stockMap);
        setClosingStockError("");
      } catch (error) {
        silentApiError(error);
        setClosingStockQtyMap({});
        setClosingStockError(
          "Closing stock could not be loaded for the selected financial year.",
        );
      } finally {
        setClosingStockLoading(false);
      }

      let restoredDraft = null;
      try {
        const rawDraft = localStorage.getItem(getPurchaseDraftKey(userData));
        restoredDraft = rawDraft ? JSON.parse(rawDraft) : null;
      } catch (error) {
        console.warn("Unable to restore purchase order draft", error);
      }

      const routeState = location?.state || {};
      const hasRoutePrefill =
        routeState?.isFromLowStock === "yes" ||
        Array.isArray(routeState?.items);

      let nextParty = {};
      let nextPartyId = "";
      let sourceRows = [emptyProductRow()];
      let nextOrderDate = todayStr();
      let nextDiscountRows = [];
      let nextChargeRows = [];

      if (hasRoutePrefill && !routePrefillAppliedRef.current) {
        routePrefillAppliedRef.current = true;

        nextPartyId = routeState?.partyId || "";
        nextParty =
          formattedParties.find(
            (party) => String(party?._id) === String(nextPartyId),
          ) ||
          formattedParties.find(
            (party) =>
              String(party?.CompanyName || "")
                .trim()
                .toLowerCase() ===
              String(routeState?.partyName || "")
                .trim()
                .toLowerCase(),
          ) ||
          {};

        nextPartyId = nextParty?._id || nextPartyId;

        sourceRows =
          Array.isArray(routeState?.items) && routeState.items.length
            ? routeState.items.map((item) => ({
                ...emptyProductRow(),
                ...item,
                productId: item?.productId?._id || item?.productId || "",
                basicPrice: firstDefined(
                  item?.basicPrice,
                  item?.price,
                  item?.lastPrice,
                  0,
                ),
              }))
            : [emptyProductRow()];
      } else if (restoredDraft) {
        nextPartyId = restoredDraft?.PartyId || "";
        nextParty =
          formattedParties.find(
            (party) => String(party?._id) === String(nextPartyId),
          ) ||
          restoredDraft?.Party ||
          {};
        sourceRows =
          Array.isArray(restoredDraft?.product) && restoredDraft.product.length
            ? restoredDraft.product
            : [emptyProductRow()];
        nextOrderDate = restoredDraft?.orderDate || todayStr();
        nextDiscountRows = Array.isArray(restoredDraft?.selectedDiscountRows)
          ? restoredDraft.selectedDiscountRows
          : [];
        nextChargeRows = Array.isArray(restoredDraft?.selectedChargesRows)
          ? restoredDraft.selectedChargesRows
          : [];
      } else if (isCustomer) {
        nextPartyId = userData?._id || "";
        nextParty =
          formattedParties.find(
            (party) => String(party?._id) === String(userData?._id),
          ) || userData;
      }

      const hydratedRows = hydrateProductRows(
        sourceRows,
        formattedProducts,
        stockMap,
      );

      setPartyId(nextPartyId);
      setParty(nextParty);
      setProduct(hydratedRows);
      setSelectedDiscountRows(nextDiscountRows);
      setSelectedChargesRows(nextChargeRows);
      setOrderDate(nextOrderDate);

      // Always generate the PO number from the complete purchase-order list.
      // Draft data must not restore an old PO number.
      const nextGeneratedNumber = buildNextPurchaseOrderNumber(purchaseOrders);
      setOrderNumber(nextGeneratedNumber);

      setDraftReady(true);
      setPageLoading(false);
    })().catch((error) => {
      console.error("CREATE PURCHASE ORDER LOAD ERROR:", error);
      if (!active) return;
      setPageLoading(false);
      setClosingStockLoading(false);
      setDraftReady(true);
    });

    return () => {
      active = false;
    };
    // Initial load only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!draftReady) return undefined;

    const timer = window.setTimeout(() => {
      const userData = JSON.parse(localStorage.getItem("userData") || "{}");
      const draftKey = getPurchaseDraftKey(userData);

      const hasEnteredData =
        !!PartyId ||
        product.some((row) => !!row?.productId) ||
        selectedChargesRows.length > 0 ||
        selectedDiscountRows.length > 0;

      if (!hasEnteredData) {
        localStorage.removeItem(draftKey);
        return;
      }

      try {
        localStorage.setItem(
          draftKey,
          JSON.stringify({
            version: 1,
            savedAt: new Date().toISOString(),
            OrderNumber,
            orderDate,
            PartyId,
            Party,
            product,
            selectedChargesRows,
            selectedDiscountRows,
          }),
        );
      } catch (error) {
        console.warn("Unable to save purchase order draft", error);
      }
    }, 250);

    return () => window.clearTimeout(timer);
  }, [
    draftReady,
    OrderNumber,
    orderDate,
    PartyId,
    Party,
    product,
    selectedChargesRows,
    selectedDiscountRows,
  ]);

  useEffect(() => {
    const today = new Date();
    const month = today.getMonth();
    const year = today.getFullYear();

    if (month !== 3) {
      setShowFYPrefixNotice(false);
      return;
    }

    const label = `${year}-${year + 1}`;
    const key = `prefix_suffix_done_${label}`;
    setCurrentFYLabel(label);
    setShowFYPrefixNotice(localStorage.getItem(key) !== "true");
  }, []);

  useEffect(() => {
    const refresh = () => {
      const userData = JSON.parse(localStorage.getItem("userData") || "{}");
      if (!userData?.database || !ProductList.length) return;

      refreshClosingStock({
        userData,
        products: ProductList,
        updateExistingRows: true,
      });
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") refresh();
    };

    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ProductList]);

  /* ---------------- party handlers ---------------- */

  const handleSelectionParty = (_selectedList, selectedItem) => {
    setPartyId(selectedItem?._id || "");
    setParty(selectedItem || {});

    multiselectRefs.current.forEach((r) => r?.current?.resetSelectedValues?.());

    setProduct([emptyProductRow()]);
    setSelectedChargesRows([]);
    setSelectedDiscountRows([]);
  };

  const onRemoveParty = () => {
    setPartyId("");
    setParty({});

    multiselectRefs.current.forEach((r) => r?.current?.resetSelectedValues?.());

    setProduct([emptyProductRow()]);
    setSelectedChargesRows([]);
    setSelectedDiscountRows([]);
  };

  /* ---------------- product handlers ---------------- */

  const handleSelection = (_selectedList, selectedItem, index) => {
    const stock = getClosingStockLookup(selectedItem, closingStockQtyMap);

    setProduct((previousRows) => {
      const updatedRows = [...previousRows];
      const previousRow = updatedRows[index] || emptyProductRow();

      const secondarySize = toNum(selectedItem?.secondarySize, 1) || 1;
      const gstPercentage = parseGstPct(selectedItem?.GSTRate || 0);
      const basicPrice = getPurchaseRate(selectedItem);
      const purchasePriceWithGst = getPurchasePriceIncludingGST(
        selectedItem,
        gstPercentage,
      );
      const qty = Math.max(0, toNum(previousRow?.qty, 1));

      updatedRows[index] = {
        ...emptyProductRow(),
        ...previousRow,
        productId: selectedItem?._id || "",
        productData: selectedItem || {},
        warehouse:
          stock?.warehouse ||
          normId(
            selectedItem?.warehouse?._id ||
              selectedItem?.warehouse ||
              selectedItem?.warehouseId,
          ),
        warehouseName:
          selectedItem?.warehouse?.warehouseName ||
          selectedItem?.warehouseName ||
          "",
        availableQty: Math.max(0, toNum(stock?.qty, 0)),
        wholeQty: Math.max(0, toNum(stock?.qty, 0)),
        HSN_Code: selectedItem?.HSN_Code || "",
        primaryUnit: selectedItem?.primaryUnit || "",
        secondaryUnit: selectedItem?.secondaryUnit || "",
        secondarySize,
        qty,
        GrossQty: Number((qty / secondarySize).toFixed(3)),
        gstPercentage,
        mrp: toNum(selectedItem?.Product_MRP, 0),
        purchasePriceWithGst,
        basicPrice,
        purchaseRate: basicPrice,
        taxableAmount: round2(qty * basicPrice),
      };

      return updatedRows;
    });
  };

  const handleProductDropdownChange = (event, index) => {
    const selectedId = event.target.value;

    if (!selectedId) {
      onRemoveProduct([], {}, index);
      return;
    }

    const selectedItem = ProductList.find(
      (item) => String(item?._id) === String(selectedId),
    );

    if (!selectedItem) return;

    console.log("Selected Product Information:", selectedItem);
    handleSelection([], selectedItem, index);
  };

  const onRemoveProduct = (_selectedList, _removedItem, index) => {
    setProduct((prev) => {
      const updated = [...prev];
      updated[index] = emptyProductRow();
      return updated;
    });
  };

  const handleQtyChange = (e, index) => {
    const { name, value } = e.target;
    const currentValue = toNum(value, 0);

    setProduct((prev) => {
      const updated = [...prev];
      const row = { ...(updated[index] || emptyProductRow()) };

      if (name === "GrossQty") {
        row.GrossQty = currentValue;
        row.qty = Number(
          (toNum(row.secondarySize, 1) * currentValue).toFixed(3),
        );
      } else {
        row.qty = currentValue;
        row.GrossQty = Number(
          (currentValue / toNum(row.secondarySize, 1)).toFixed(3),
        );
      }

      row.taxableAmount = round2(toNum(row.qty, 0) * toNum(row.basicPrice, 0));

      updated[index] = row;

      return updated;
    });
  };

  const handleChangePrice = (e, index) => {
    const { name, value } = e.target;
    const numericValue = Math.max(0, toNum(value, 0));

    setProduct((previousRows) => {
      const updatedRows = [...previousRows];
      const row = {
        ...(updatedRows[index] || emptyProductRow()),
      };
      const gstPercentage = parseGstPct(row?.gstPercentage || 0);

      if (name === "purchasePriceWithGst") {
        row.purchasePriceWithGst = numericValue;
        row.basicPrice = basicFromInclusivePurchasePrice(
          numericValue,
          gstPercentage,
        );
        row.purchaseRate = row.basicPrice;
      } else {
        row.basicPrice = numericValue;
        row.purchaseRate = numericValue;
        row.purchasePriceWithGst = round2(
          numericValue * (1 + gstPercentage / 100),
        );
      }

      row.taxableAmount = round2(toNum(row.qty, 0) * toNum(row.basicPrice, 0));

      updatedRows[index] = row;
      return updatedRows;
    });
  };

  const addMoreProduct = () => {
    setProduct((prev) => [...prev, emptyProductRow()]);
    multiselectRefs.current.push(React.createRef());
  };

  const removeMoreProduct = (index) => {
    setProduct((prev) => {
      const updated = [...prev];
      updated.splice(index, 1);
      return updated.length ? updated : [emptyProductRow()];
    });

    multiselectRefs.current.splice(index, 1);
  };

  /* ---------------- adjustment handlers ---------------- */

  const addChargeRow = () => {
    setSelectedChargesRows((prev) => [
      ...prev,
      {
        id: "",
        title: "",
        type: "Charges",
        discounttype: "",
        percentage: "",
        amount: "",
        __applied: 0,
      },
    ]);
  };

  const addDiscountRow = () => {
    setSelectedDiscountRows((prev) => [
      ...prev,
      {
        id: "",
        title: "",
        type: "Discount",
        discounttype: "",
        percentage: "",
        amount: "",
        __applied: 0,
      },
    ]);
  };

  const removeChargeRow = (i) => {
    const arr = [...selectedChargesRows];
    arr.splice(i, 1);
    setSelectedChargesRows(arr);
  };

  const removeDiscountRow = (i) => {
    const arr = [...selectedDiscountRows];
    arr.splice(i, 1);
    setSelectedDiscountRows(arr);
  };

  const pickMasterRow = (rowType, idx, id) => {
    const master = chargesMaster.find((x) => String(x?._id) === String(id));
    if (!master) return;

    const rowData = {
      id: master._id,
      title: master.title,
      type: master.type,
      discounttype: master.discounttype,
      percentage: master.percentage || "",
      amount: master.amount || "",
      __applied: 0,
    };

    if (rowType === "Charges") {
      const arr = [...selectedChargesRows];
      arr[idx] = rowData;
      setSelectedChargesRows(arr);
    } else {
      const arr = [...selectedDiscountRows];
      arr[idx] = rowData;
      setSelectedDiscountRows(arr);
    }
  };

  const changeRowField = (rowType, idx, key, value) => {
    const norm = (k, v) => {
      if (k === "percentage" || k === "amount") {
        if (v === "" || v === null) return "";
        const n = Number(v);
        return Number.isNaN(n) ? "" : n;
      }

      return v;
    };

    if (rowType === "Charges") {
      const arr = [...selectedChargesRows];
      arr[idx] = {
        ...arr[idx],
        [key]: norm(key, value),
      };
      setSelectedChargesRows(arr);
    } else {
      const arr = [...selectedDiscountRows];
      arr[idx] = {
        ...arr[idx],
        [key]: norm(key, value),
      };
      setSelectedDiscountRows(arr);
    }
  };

  const onAddAdjustmentSelect = (e) => {
    const id = e.target.value;
    if (!id) return;

    e.target.value = "";

    const master = chargesMaster.find((x) => String(x?._id) === String(id));
    if (!master) return;

    const rowData = {
      id: master._id,
      title: master.title,
      type: master.type,
      discounttype: master.discounttype,
      percentage: master.percentage || "",
      amount: master.amount || "",
      __applied: 0,
    };

    if (
      String(master.type || "")
        .toLowerCase()
        .includes("discount")
    ) {
      setSelectedDiscountRows((prev) => [...prev, rowData]);
    } else {
      setSelectedChargesRows((prev) => [...prev, rowData]);
    }
  };

  /* ---------------- submit ---------------- */

  const handleProceed = async () => {
    try {
      setLoading(true);

      if (!PartyId) {
        swal("Select a party first.");
        return;
      }

      const validProducts = product
        .map((row, index) => ({ ...row, _rowIndex: index }))
        .filter((row) => !!row?.productId);

      if (!validProducts.length) {
        swal("Select at least one product.");
        return;
      }

      const invalidQuantityRow = validProducts.find(
        (row) => toNum(row?.qty, 0) <= 0,
      );
      if (invalidQuantityRow) {
        swal("Product quantity must be greater than zero.");
        return;
      }

      const invalidRateRow = validProducts.find(
        (row) => toNum(row?.basicPrice, 0) < 0,
      );
      if (invalidRateRow) {
        swal("Product price cannot be negative.");
        return;
      }

      const duplicateProductIds = validProducts
        .map((row) => String(row?.productId || ""))
        .filter((id, index, array) => !!id && array.indexOf(id) !== index);

      if (duplicateProductIds.length) {
        swal(
          "The same product is selected more than once. Please keep one row per product.",
        );
        return;
      }

      const userData = JSON.parse(localStorage.getItem("userData") || "{}");
      const selectedDatabase = getSelectedDatabase(userData);

      // Re-read the full purchase-order list immediately before saving.
      // The number shown and sent is always total order count + 1.
      const existingOrders = await loadPurchaseOrderHistory(
        userData?._id,
        selectedDatabase,
      );

      const finalOrderNumber = buildNextPurchaseOrderNumber(existingOrders);
      setOrderNumber(finalOrderNumber);

      const discountDetails = buildDiscountDetailsPayload(
        appliedDiscountRows,
        appliedChargeRows,
      );

      const orderItemsPayload = validProducts.map((item) => {
        const qty = Math.max(0, toNum(item?.qty, 0));
        const basicPrice = Math.max(0, toNum(item?.basicPrice, 0));
        const gstRow = gstData?.gstDetails?.[item?._rowIndex] || {};

        return {
          productId: item?.productId,
          productData: item?.productData,
          warehouse:
            normId(item?.warehouse) ||
            normId(item?.productData?.warehouse?._id) ||
            normId(item?.productData?.warehouse) ||
            "",
          warehouseName:
            item?.warehouseName ||
            item?.productData?.warehouse?.warehouseName ||
            item?.productData?.warehouseName ||
            "",
          discountPercentage: toNum(item?.disCountPercentage, 0),
          availableQty: Math.max(0, toNum(item?.availableQty, 0)),
          qty,
          price: basicPrice,
          basicPrice,
          purchaseRate: basicPrice,
          purchasePriceWithGst: round2(item?.purchasePriceWithGst || 0),
          mrp: round2(item?.mrp || 0),
          totalPrice: round2(qty * basicPrice),
          primaryUnit: item?.primaryUnit,
          secondaryUnit: item?.secondaryUnit,
          secondarySize: toNum(item?.secondarySize, 1) || 1,
          GrossQty: toNum(item?.GrossQty, 0),
          sgstRate: toNum(gstRow?.sgstRate, 0),
          cgstRate: toNum(gstRow?.cgstRate, 0),
          igstRate: toNum(gstRow?.igstRate, 0),
          gstPercentage: parseGstPct(item?.gstPercentage),
          grandTotal: toNum(gstRow?.grandTotal, 0),
          taxableAmount: toNum(gstRow?.taxable, 0),
          rawTaxableAmount: toNum(gstRow?.rawTaxable, round2(qty * basicPrice)),
          totalPriceWithDiscount: toNum(gstRow?.grandTotal, 0),
          igstTaxType: gstData?.Tax?.IgstTaxType,
        };
      });

      const payload = {
        userId: userData?._id,
        partyId: PartyId,
        database: selectedDatabase,
        financialYear:
          localStorage.getItem("financialYearSelected") ||
          getFinancialYearLabel(orderDate),

        SuperAdmin:
          Context?.CompanyDetails?.created_by ||
          userData?.created_by ||
          userData?._id,

        // IMPORTANT: all PO-number fields carry the exact same value.
        // The backend must save this exact value and must not recalculate it.
        invoice: finalOrderNumber,
        poNumber: finalOrderNumber,
        purchaseOrderNo: finalOrderNumber,
        orderNumber: finalOrderNumber,
        fullName: Party?.CompanyName || Party?.fullName || "",
        address: Party?.address,
        MobileNo: Party?.contactNumber || Party?.MobileNo || Party?.mobile,
        pincode: Party?.pincode,
        state: Party?.State || Party?.state,
        city: Party?.City || Party?.city,
        regionType: Party?.regionType,

        grandTotal: round2(gstData?.Tax?.GrandTotal || 0),
        grossBeforeRound: round2(gstData?.Tax?.GrossBeforeRound || 0),
        roundOff: round2(gstData?.Tax?.RoundOff || 0),
        roundOffSigned: round2(gstData?.Tax?.RoundOffSigned || 0),

        // Basic Total is before bill-level discount/charge.
        basicTotal: round2(gstData?.Tax?.BasicTotal || 0),
        basicPrice: round2(gstData?.Tax?.BasicTotal || 0),

        // Amount/Taxable Value is after discounts and after charges.
        amount: round2(gstData?.Tax?.Amount || 0),
        taxableAmount: round2(gstData?.Tax?.Amount || 0),
        discountTotal: round2(gstData?.adjustments?.discountTotal || 0),
        chargesTotal: round2(gstData?.adjustments?.chargesTotal || 0),

        sgstTotal: round2(gstData?.Tax?.SgstTotal || 0),
        cgstTotal: round2(gstData?.Tax?.CgstTotal || 0),
        igstTotal: round2(gstData?.Tax?.IgstTotal || 0),
        igstTaxType: gstData?.Tax?.IgstTaxType,

        ChargesCgst: round2(gstData?.Tax?.ChargesCgst || 0),
        ChargesSgst: round2(gstData?.Tax?.ChargesSgst || 0),
        ChargesIgst: round2(gstData?.Tax?.ChargesIgst || 0),

        gstDetails: gstData?.gstDetails || [],
        hsnData: hsnSummary?.rows || [],
        discountDetails,

        orderItems: orderItemsPayload,
        date: orderDate,
        DateofDelivery: "",

        coolieAndCartage: round2(gstData?.adjustments?.chargesTotal || 0),
        transportationCost: 0,
        labourCost: 0,
        localFreight: 0,
        miscellaneousCost: 0,
        tax: round2(
          toNum(gstData?.Tax?.SgstTotal, 0) +
            toNum(gstData?.Tax?.CgstTotal, 0) +
            toNum(gstData?.Tax?.IgstTotal, 0),
        ),
        maxGstPercentage: round2(gstData?.Tax?.maxGst || 0),
      };

      console.log("FINAL PURCHASE ORDER PAYLOAD:", payload);

      const response = await SavePurchaseOrder(payload);

      console.log("SAVE PURCHASE ORDER RESPONSE:", response);

      if (response?.status === false) {
        swal(
          response?.message ||
            "Something went wrong while creating purchase order",
        );
        return;
      }

      localStorage.removeItem(getPurchaseDraftKey(userData));

      swal("Purchase Order Created Successfully");
      navigate(-1);
    } catch (error) {
      console.log("SAVE PURCHASE ORDER ERROR:", error);

      const nextPoNumber =
        error?.response?.data?.nextPoNumber ||
        error?.response?.data?.nextPONumber ||
        "";

      if (error?.response?.status === 409 && nextPoNumber) {
        setOrderNumber(String(nextPoNumber));
        swal(
          error?.response?.data?.message ||
            `PO number conflict. The next PO number is ${nextPoNumber}. Please submit again.`,
        );
        return;
      }

      swal(
        error?.response?.data?.message ||
          error?.message ||
          "Something went wrong while creating purchase order",
      );
    } finally {
      setLoading(false);
    }
  };

  const clearPurchaseOrderData = async () => {
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");
    const selectedDatabase = getSelectedDatabase(userData);
    const isCustomer = userData?.rolename?.roleName === "Customer";

    localStorage.removeItem(getPurchaseDraftKey(userData));

    partyMultiselectRef.current?.resetSelectedValues?.();
    multiselectRefs.current.forEach((ref) =>
      ref?.current?.resetSelectedValues?.(),
    );

    setPartyId(isCustomer ? userData?._id || "" : "");
    setParty(isCustomer ? userData : {});
    setProduct([emptyProductRow()]);
    setSelectedChargesRows([]);
    setSelectedDiscountRows([]);
    setOrderDate(todayStr());

    const existingOrders = await loadPurchaseOrderHistory(
      userData?._id,
      selectedDatabase,
    );
    setOrderNumber(buildNextPurchaseOrderNumber(existingOrders));

    swal("Purchase order data cleared");
  };

  const handleFYPrefixDone = (event) => {
    if (!event.target.checked || !currentFYLabel) return;

    localStorage.setItem(`prefix_suffix_done_${currentFYLabel}`, "true");
    setShowFYPrefixNotice(false);
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    await handleProceed();
  };

  /* ---------------- loading ---------------- */

  if (pageLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary">
          Loading...
        </Spinner>
      </div>
    );
  }

  /* ---------------- render EXACT CreateOrder design classes ---------------- */

  return (
    <div className="invoice-ui" style={{ position: "relative" }}>
      {showFYPrefixNotice && (
        <div
          style={{
            background: "#fff7ed",
            border: "1px solid #fb923c",
            color: "#9a3412",
            padding: "10px 14px",
            borderRadius: "10px",
            marginBottom: "12px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "12px",
            fontSize: "14px",
            fontWeight: 600,
          }}
        >
          <span>
            Attention: Kindly change your Prefix and Suffix as per the Financial
            Year {currentFYLabel}.
          </span>

          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              cursor: "pointer",
              margin: 0,
              whiteSpace: "nowrap",
            }}
          >
            <input type="checkbox" onChange={handleFYPrefixDone} />
            Done
          </label>
        </div>
      )}

      <Card>
        <Row style={styles.headerRow}>
          <Col>
            <h1 style={styles.title}>Create Purchase Order</h1>
          </Col>

          <Col style={{ ...styles.right, gap: 8 }}>
            <Button
              color="warning"
              type="button"
              onClick={clearPurchaseOrderData}
            >
              Clear
            </Button>
            <Button color="danger" type="button" onClick={() => navigate(-1)}>
              Back
            </Button>
          </Col>
        </Row>

        <CardBody>
          <Form onSubmit={submitHandler}>
            <div className="top-strip">
              {!PartyLogin && (
                <div className="box party-box">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <Label>
                      Choose Party <span style={{ color: "red" }}>*</span>
                    </Label>

                    <div style={{ display: "flex", gap: 6 }}>
                      <Button
                        size="sm"
                        color="secondary"
                        type="button"
                        onClick={() => {
                          if (PartyId) {
                            navigate(`/app/customers/create/${PartyId}`);
                          } else {
                            swal("Select a party first");
                          }
                        }}
                      >
                        Edit
                      </Button>

                      <Button
                        size="sm"
                        color="primary"
                        type="button"
                        onClick={() => navigate(`/app/customers/create/0`)}
                      >
                        New
                      </Button>
                    </div>
                  </div>

                  <Multiselect
                    ref={partyMultiselectRef}
                    required
                    selectionLimit={1}
                    isObject
                    options={PartyList}
                    selectedValues={Party?._id ? [Party] : []}
                    onSelect={(selectedList, selectedItem) => {
                      console.log(
                        "CUSTOMER DROPDOWN SELECTED LIST:",
                        selectedList,
                      );
                      console.log(
                        "CUSTOMER DROPDOWN SELECTED ITEM:",
                        selectedItem,
                      );
                      handleSelectionParty(selectedList, selectedItem);
                    }}
                    onRemove={(selectedList, removedItem) => {
                      console.log(
                        "CUSTOMER DROPDOWN REMOVED LIST:",
                        selectedList,
                      );
                      console.log(
                        "CUSTOMER DROPDOWN REMOVED ITEM:",
                        removedItem,
                      );
                      onRemoveParty(selectedList, removedItem);
                    }}
                    displayValue="displayLabel"
                    avoidHighlightFirstOption
                    placeholder="Select Party"
                  />

                  <small style={{ display: "inline-block", marginTop: 5 }}>
                    <p style={{ margin: 0 }}>
                      Region: <b>{Party?.regionType || "-"}</b>
                    </p>
                    <p style={{ margin: 0 }}>
                      Email: <b>{getEmail(Party) || "-"}</b>
                    </p>
                  </small>
                </div>
              )}

              <div className="box orderdate-box">
                <Label>
                  Order Date <span style={{ color: "red" }}>*</span>
                </Label>

                <Input
                  required
                  type="date"
                  name="orderDate"
                  value={orderDate}
                  onChange={(e) => setOrderDate(e.target.value)}
                />
              </div>

              <div className="box orderno-box">
                <Label>
                  PO No <span style={{ color: "red" }}>*</span>
                </Label>

                <Input
                  required
                  readOnly
                  type="text"
                  placeholder="PO No"
                  name="OrderNumber"
                  value={OrderNumber}
                />
              </div>

              {/* <div className="box balance-box">
                <Label>Selected Database</Label>
                <Input readOnly type="text" value={selectedDbText || ""} />
              </div> */}
            </div>

            {closingStockError && (
              <div
                style={{
                  color: "red",
                  fontSize: 12,
                  marginTop: 8,
                  fontWeight: 600,
                }}
              >
                {closingStockError}
              </div>
            )}

            {product?.length > 0 &&
              product?.map((p, index) => {
                const lineBasic = round2(
                  Number(p?.qty || 0) * Number(p?.basicPrice || 0),
                );

                return (
                  <div key={index} className="prod-row mt-2">
                    <div className="box prod-select-box">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <Label>
                          Select Product <span style={{ color: "red" }}>*</span>
                        </Label>

                        <div style={{ display: "flex", gap: 6 }}>
                          <Button
                            size="sm"
                            color="secondary"
                            type="button"
                            onClick={() => {
                              const pid = product?.[index]?.productId;
                              if (pid) {
                                navigate(`/app/products/edit/${pid}`);
                              } else {
                                swal("Select a product first");
                              }
                            }}
                          >
                            Edit
                          </Button>

                          <Button
                            size="sm"
                            color="primary"
                            type="button"
                            onClick={() => navigate(`/app/products/create`)}
                          >
                            New
                          </Button>
                        </div>
                      </div>

                      <Input
                        required
                        type="select"
                        disabled={closingStockLoading}
                        value={p?.productId || ""}
                        onChange={(e) => handleProductDropdownChange(e, index)}
                        style={{ fontWeight: 600 }}
                      >
                        <option value="">
                          {closingStockLoading
                            ? "Loading closing stock..."
                            : "Select Product"}
                        </option>
                        {ProductList.map((item) => (
                          <option key={item?._id} value={item?._id}>
                            {item?.Product_Title}
                          </option>
                        ))}
                      </Input>
                    </div>

                    <div className="box hsn-box">
                      <Label>HSN Code</Label>
                      <Input readOnly value={p?.HSN_Code || ""} />
                    </div>

                    <div className="box grossunit-box">
                      <Label>Gross Unit</Label>
                      <Input readOnly value={p?.secondaryUnit || ""} />
                    </div>

                    <div className="box grossqty-box">
                      <Label>Gross Qty</Label>
                      <Input
                        required
                        type="number"
                        step="any"
                        name="GrossQty"
                        value={p?.GrossQty || 0}
                        onChange={(e) => handleQtyChange(e, index)}
                      />
                    </div>

                    <div className="box availqty-box">
                      <Label>Avail Qty *</Label>
                      <Input
                        readOnly
                        value={Number(p?.availableQty || 0).toFixed(2)}
                        style={{ fontWeight: 700 }}
                      />
                      <div
                        style={{
                          fontSize: 10,
                          color: "#64748b",
                          marginTop: 2,
                        }}
                      >
                        Closing stock
                      </div>
                    </div>

                    <div className="box netqty-box">
                      <Label>Net Qty *</Label>
                      <Input
                        required
                        type="number"
                        name="qty"
                        min={1}
                        value={p?.qty || 1}
                        onChange={(e) => handleQtyChange(e, index)}
                      />
                    </div>

                    <div className="box netunit-box">
                      <Label>Net Unit</Label>
                      <Input disabled value={p?.primaryUnit || ""} />
                    </div>

                    <div className="box mrp-box">
                      <Label>MRP</Label>
                      <Input readOnly value={Number(p?.mrp || 0).toFixed(2)} />
                    </div>

                    <div className="box saleprice-box">
                      <Label>Purchase Price (incl GST)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        name="purchasePriceWithGst"
                        value={p?.purchasePriceWithGst || 0}
                        onChange={(e) => handleChangePrice(e, index)}
                      />
                    </div>

                    <div className="box basicprice-box">
                      <Label>Basic Price (ex GST)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        name="basicPrice"
                        value={p?.basicPrice || 0}
                        onChange={(e) => handleChangePrice(e, index)}
                      />
                    </div>

                    <div className="box gst-box">
                      <Label>GST %</Label>
                      <Input disabled value={parseGstPct(p?.gstPercentage)} />
                    </div>

                    <div className="box basictotal-box">
                      <Label>Basic Total</Label>
                      <Input disabled value={lineBasic.toFixed(2)} />
                    </div>

                    <div className="box remove-box">
                      {product?.length > 1 ? (
                        <Button
                          type="button"
                          color="danger"
                          className="btn-xxs"
                          onClick={() => removeMoreProduct(index)}
                          style={{ marginTop: 24 }}
                          aria-label="Remove row"
                        >
                          X
                        </Button>
                      ) : null}
                    </div>
                  </div>
                );
              })}

            <div className="d-flex justify-content-end mt-2">
              <Button
                color="primary"
                size="sm"
                type="button"
                onClick={addMoreProduct}
              >
                Add Product
              </Button>
            </div>

            <div className="justify-content-total jt-container mt-3">
              <div className="left-tables jt-left">
                <div className="mini-title jt-title">Bill Adjustments</div>

                <div className="adjust-toolbar jt-toolbar">
                  <div className="box adjust-addselect jt-addselect">
                    <Label className="jt-addselect-label">
                      Add Discount / Charge
                    </Label>

                    <Input
                      type="select"
                      className="jt-addselect-input"
                      onChange={onAddAdjustmentSelect}
                    >
                      <option value="">-- Select from master --</option>
                      {chargesMaster.map((x) => (
                        <option key={x._id} value={x._id}>
                          {x.title} ({x.type})
                        </option>
                      ))}
                    </Input>
                  </div>

                  <Button
                    size="sm"
                    color="success"
                    className="btn-xxs jt-btn jt-btn-discount"
                    type="button"
                    onClick={addDiscountRow}
                  >
                    + Discount
                  </Button>

                  <Button
                    size="sm"
                    color="primary"
                    className="btn-xxs jt-btn jt-btn-charge"
                    type="button"
                    onClick={addChargeRow}
                  >
                    + Charge
                  </Button>
                </div>

                <div className="adjust-table jt-table">
                  <div className="adj-row adj-head jt-row jt-head">
                    <div className="jt-h-idx">#</div>
                    <div className="jt-h-type">Type</div>
                    <div className="jt-h-title">Title / Master</div>
                    <div className="jt-h-method">Method</div>
                    <div className="jt-h-pct">%</div>
                    <div className="jt-h-amt">Amount</div>
                    <div className="jt-h-applied">Applied</div>
                    <div className="jt-h-action">Action</div>
                  </div>

                  {selectedDiscountRows.map((r, i) => {
                    const applied = appliedDiscountRows[i] || r;
                    const isPct =
                      String(r.discounttype || "").toLowerCase() ===
                      "percentage";

                    return (
                      <div
                        className="adj-row jt-row jt-discount-row"
                        key={`disc-${i}`}
                      >
                        <div className="c-idx jt-idx">{i + 1}</div>
                        <div className="c-type jt-type">Discount</div>

                        <div className="c-title jt-title-cell">
                          <Input
                            type="select"
                            className="jt-input jt-select jt-title-select"
                            value={r.id || ""}
                            onChange={(e) =>
                              pickMasterRow("Discount", i, e.target.value)
                            }
                          >
                            <option value="">-- Select from master --</option>
                            {chargesMaster
                              .filter((m) =>
                                String(m.type || "")
                                  .toLowerCase()
                                  .includes("discount"),
                              )
                              .map((m) => (
                                <option key={m._id} value={m._id}>
                                  {m.title}
                                </option>
                              ))}
                          </Input>
                        </div>

                        <div className="c-method jt-method-cell">
                          <Input
                            type="select"
                            className="jt-input jt-select jt-method-select"
                            value={r.discounttype || ""}
                            onChange={(e) =>
                              changeRowField(
                                "Discount",
                                i,
                                "discounttype",
                                e.target.value,
                              )
                            }
                          >
                            <option value="">Choose</option>
                            <option value="Percentage">Percentage</option>
                            <option value="Amount">Amount</option>
                          </Input>
                        </div>

                        <div className="c-pct jt-pct-cell">
                          <Input
                            type="number"
                            inputMode="decimal"
                            placeholder="%"
                            className="jt-input jt-number jt-pct-input"
                            disabled={!isPct}
                            value={r.percentage === "" ? "" : r.percentage}
                            onChange={(e) =>
                              changeRowField(
                                "Discount",
                                i,
                                "percentage",
                                e.target.value,
                              )
                            }
                          />
                        </div>

                        <div className="c-amt jt-amt-cell">
                          <Input
                            type="number"
                            inputMode="decimal"
                            placeholder="₹"
                            className="jt-input jt-number jt-amt-input"
                            disabled={isPct}
                            value={r.amount === "" ? "" : r.amount}
                            onChange={(e) =>
                              changeRowField(
                                "Discount",
                                i,
                                "amount",
                                e.target.value,
                              )
                            }
                          />
                        </div>

                        <div className="c-applied jt-applied-cell">
                          <Input
                            readOnly
                            className="jt-input jt-readonly jt-applied-input"
                            value={applied.__applied ?? 0}
                          />
                        </div>

                        <div className="c-action jt-action-cell">
                          <Button
                            color="danger"
                            size="sm"
                            className="btn-xxs jt-btn jt-delete-btn"
                            type="button"
                            onClick={() => removeDiscountRow(i)}
                            aria-label="Delete"
                          >
                            🗑
                          </Button>
                        </div>
                      </div>
                    );
                  })}

                  {selectedChargesRows.map((r, i) => {
                    const applied = appliedChargeRows[i] || r;
                    const isPct =
                      String(r.discounttype || "").toLowerCase() ===
                      "percentage";

                    return (
                      <div
                        className="adj-row jt-row jt-charge-row"
                        key={`charge-${i}`}
                      >
                        <div className="c-idx jt-idx">{i + 1}</div>
                        <div className="c-type jt-type">Charge</div>

                        <div className="c-title jt-title-cell">
                          <Input
                            type="select"
                            className="jt-input jt-select jt-title-select"
                            value={r.id || ""}
                            onChange={(e) =>
                              pickMasterRow("Charges", i, e.target.value)
                            }
                          >
                            <option value="">-- Select from master --</option>
                            {chargesMaster
                              .filter((m) =>
                                String(m.type || "")
                                  .toLowerCase()
                                  .includes("charge"),
                              )
                              .map((m) => (
                                <option key={m._id} value={m._id}>
                                  {m.title}
                                </option>
                              ))}
                          </Input>
                        </div>

                        <div className="c-method jt-method-cell">
                          <Input
                            type="select"
                            className="jt-input jt-select jt-method-select"
                            value={r.discounttype || ""}
                            onChange={(e) =>
                              changeRowField(
                                "Charges",
                                i,
                                "discounttype",
                                e.target.value,
                              )
                            }
                          >
                            <option value="">Choose</option>
                            <option value="Percentage">Percentage</option>
                            <option value="Amount">Amount</option>
                          </Input>
                        </div>

                        <div className="c-pct jt-pct-cell">
                          <Input
                            type="number"
                            inputMode="decimal"
                            placeholder="%"
                            className="jt-input jt-number jt-pct-input"
                            disabled={!isPct}
                            value={r.percentage === "" ? "" : r.percentage}
                            onChange={(e) =>
                              changeRowField(
                                "Charges",
                                i,
                                "percentage",
                                e.target.value,
                              )
                            }
                          />
                        </div>

                        <div className="c-amt jt-amt-cell">
                          <Input
                            type="number"
                            inputMode="decimal"
                            placeholder="₹"
                            className="jt-input jt-number jt-amt-input"
                            disabled={isPct}
                            value={r.amount === "" ? "" : r.amount}
                            onChange={(e) =>
                              changeRowField(
                                "Charges",
                                i,
                                "amount",
                                e.target.value,
                              )
                            }
                          />
                        </div>

                        <div className="c-applied jt-applied-cell">
                          <Input
                            readOnly
                            className="jt-input jt-readonly jt-applied-input"
                            value={applied.__applied ?? 0}
                          />
                        </div>

                        <div className="c-action jt-action-cell">
                          <Button
                            color="danger"
                            size="sm"
                            className="btn-xxs jt-btn jt-delete-btn"
                            type="button"
                            onClick={() => removeChargeRow(i)}
                            aria-label="Delete"
                          >
                            🗑
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {hsnSummary.rows.length > 0 && (
                <div className="mt-2 jt-hsn-block">
                  <div className="mini-title jt-hsn-title">HSN Summary</div>

                  <Table bordered size="sm" className="jt-hsn-table">
                    <thead className="jt-hsn-thead">
                      <tr className="jt-hsn-headrow">
                        <th className="jt-hsn-th jt-hsn-col-hsn">HSN/SAC</th>
                        <th className="jt-hsn-th jt-hsn-col-taxable text-end">
                          TAXABLE VALUE
                        </th>
                        <th className="jt-hsn-th jt-hsn-col-rate text-center">
                          TAX RATE
                        </th>

                        {!hsnSummary.isIGST && (
                          <th className="jt-hsn-th jt-hsn-col-cgst text-end">
                            CGST
                          </th>
                        )}

                        {!hsnSummary.isIGST && (
                          <th className="jt-hsn-th jt-hsn-col-sgst text-end">
                            SGST
                          </th>
                        )}

                        {hsnSummary.isIGST && (
                          <th className="jt-hsn-th jt-hsn-col-igst text-end">
                            IGST
                          </th>
                        )}

                        <th className="jt-hsn-th jt-hsn-col-total text-end">
                          TOTAL
                        </th>
                      </tr>
                    </thead>

                    <tbody className="jt-hsn-tbody">
                      {hsnSummary.rows.map((r, i) => (
                        <tr
                          className="jt-hsn-row"
                          key={`hsn-${r.kind}-${r.hsn}-${i}`}
                        >
                          <td className="jt-hsn-td jt-hsn-col-hsn">{r.hsn}</td>
                          <td className="jt-hsn-td jt-hsn-col-taxable text-end">
                            {r.taxable.toFixed(2)}
                          </td>
                          <td className="jt-hsn-td jt-hsn-col-rate text-center">
                            {`${r.ratePct}%`}
                          </td>

                          {!hsnSummary.isIGST && (
                            <td className="jt-hsn-td jt-hsn-col-cgst text-end">
                              {(r.cgst || 0).toFixed(2)}
                            </td>
                          )}

                          {!hsnSummary.isIGST && (
                            <td className="jt-hsn-td jt-hsn-col-sgst text-end">
                              {(r.sgst || 0).toFixed(2)}
                            </td>
                          )}

                          {hsnSummary.isIGST && (
                            <td className="jt-hsn-td jt-hsn-col-igst text-end">
                              {(r.igst || 0).toFixed(2)}
                            </td>
                          )}

                          <td className="jt-hsn-td jt-hsn-col-total text-end">
                            {r.total.toFixed(2)}
                          </td>
                        </tr>
                      ))}

                      <tr className="jt-hsn-row jt-hsn-totalrow">
                        <td className="jt-hsn-td jt-hsn-col-hsn jt-bold">
                          TOTAL
                        </td>
                        <td className="jt-hsn-td jt-bold text-end">
                          {round2(hsnSummary.totals.taxable).toFixed(2)}
                        </td>
                        <td />

                        {!hsnSummary.isIGST && (
                          <td className="jt-hsn-td jt-bold text-end">
                            {round2(hsnSummary.totals.cgst).toFixed(2)}
                          </td>
                        )}

                        {!hsnSummary.isIGST && (
                          <td className="jt-hsn-td jt-bold text-end">
                            {round2(hsnSummary.totals.sgst).toFixed(2)}
                          </td>
                        )}

                        {hsnSummary.isIGST && (
                          <td className="jt-hsn-td jt-bold text-end">
                            {round2(hsnSummary.totals.igst).toFixed(2)}
                          </td>
                        )}

                        <td className="jt-hsn-td jt-bold text-end">
                          {round2(hsnSummary.totals.total).toFixed(2)}
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </div>
              )}

              <div className="right-summary jt-right">
                <ul className="totals-ul jt-totals">
                  <li className="jt-total-row">
                    <span className="jt-total-label">Basic Total</span>
                    <strong className="jt-total-val">
                      {Number(sumLineBasics(product)).toFixed(2)}
                    </strong>
                  </li>

                  <li className="jt-total-row">
                    <span className="jt-total-label">Discount Total</span>
                    <strong className="jt-total-val">
                      {Number(gstData?.adjustments?.discountTotal || 0).toFixed(
                        2,
                      )}
                    </strong>
                  </li>

                  <li className="jt-total-row">
                    <span className="jt-total-label">Charges Total</span>
                    <strong className="jt-total-val">
                      {Number(gstData?.adjustments?.chargesTotal || 0).toFixed(
                        2,
                      )}
                    </strong>
                  </li>

                  <li className="jt-total-row">
                    <span className="jt-total-label">Taxable Value</span>
                    <strong className="jt-total-val">
                      {Number(gstData?.Tax?.Amount || 0).toFixed(2)}
                    </strong>
                  </li>

                  {gstData?.Tax?.IgstTaxType ? (
                    <li className="jt-total-row">
                      <span className="jt-total-label">IGST</span>
                      <strong className="jt-total-val">
                        {Number(gstData?.Tax?.IgstTotal || 0).toFixed(2)}
                      </strong>
                    </li>
                  ) : (
                    <>
                      <li className="jt-total-row">
                        <span className="jt-total-label">SGST</span>
                        <strong className="jt-total-val">
                          {Number(gstData?.Tax?.SgstTotal || 0).toFixed(2)}
                        </strong>
                      </li>

                      <li className="jt-total-row">
                        <span className="jt-total-label">CGST</span>
                        <strong className="jt-total-val">
                          {Number(gstData?.Tax?.CgstTotal || 0).toFixed(2)}
                        </strong>
                      </li>
                    </>
                  )}

                  <li className="jt-total-row">
                    <span className="jt-total-label">RoundOff</span>
                    <strong className="jt-total-val">
                      {Number(gstData?.Tax?.RoundOff || 0).toFixed(2)}
                    </strong>
                  </li>

                  <hr className="jt-hr" />

                  <li className="jt-total-row jt-grand">
                    <span className="jt-total-label">Grand Total</span>
                    <strong className="jt-total-val">
                      {Number(gstData?.Tax?.GrandTotal || 0).toFixed(2)}
                    </strong>
                  </li>
                </ul>

                <div className="d-flex justify-content-end jt-submit">
                  <Button
                    color="primary"
                    type="submit"
                    className="mt-2 jt-submit-btn"
                    disabled={Loading}
                  >
                    {Loading ? "Submitting..." : "Submit"}
                  </Button>
                </div>
              </div>
            </div>
          </Form>
        </CardBody>
      </Card>
    </div>
  );
};

export default CreatePurchaseOrder;
